// src/services/aeo/index.ts
//
// AEO (Answer Engine Optimization) Assessment API client —
// ourteam-frontend#3065, BE epic ourteam-backend#3618. Contract and JSON
// shapes are documented in ./types.ts and mirror the ticket exactly.
//
// Auth model — UNRESOLVED, and the reason this client cannot go live yet.
// The AEO routes on the platform BE are gated by an `X-AEO-Secret` header
// (`_check_secret` in app/interface/routers/aeo.py). That secret must NEVER
// ship in the client bundle — anything in a web page is public — so this layer
// sends only the normal `Authorization: Bearer` session token. Until the BE
// either accepts that session token on the AEO routes or exposes a
// secret-injecting passthrough, every live call answers 401. Tracked in
// ourteam-backend#3742.
//
// Base path: the routes are mounted at `/api/v1` (aeo.router carries
// `prefix="/api/v1"` and is included at the app root in
// app/interface/main.py) — NOT under `/aeo`, which was this client's original
// guess and returned 404 for every call on staging.
//
// Readiness flag: because the auth question above is open, live calls stay
// behind `VITE_ENABLE_OURA_LIVE_API`. With the flag unset (today, every
// environment) every function serves fixture responses from ./devMock, so the
// Oura screens stay previewable and staging does not fill Sentry with 401s.
// Flip the flag to "true" per-environment the moment the BE lands; nothing
// else here changes. Delete the flag and devMock.ts once it is live everywhere.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import * as devMock from "./devMock";
import { readPublishRefusal } from "./publishRefusal";
import type { PublishRefusal } from "./deliveryTypes";
import type {
    AeoQuestionCorrection,
    AeoVerdictEngine,
    AeoVerdictReason,
    AeoGoogleAiRefreshJob,
    AeoGoogleAiRefreshStart,
    AeoGoogleAiRefreshState,
    AeoStageRerunJob,
    AeoStageRerunStart,
    AeoAssessment,
    AeoAssessmentStatus,
    AeoBusiness,
    AeoListStage,
    AeoStageCounts,
    AeoBusinessDetail,
    AeoScoreHistoryEntry,
    AeoBusinessSpend,
    AeoBusinessDetailResult,
    AeoCancelResult,
    AeoConnector,
    AeoCreateAssessmentAccepted,
    AeoCreateAssessmentRequest,
    AeoCreateResult,
    AeoDetectedBusiness,
    AeoDraft,
    AeoDraftCreateRequest,
    AeoReadabilityBlock,
    AeoReadabilityRefreshJob,
    AeoReadabilityRefreshMode,
    AeoReadabilityRefreshStart,
    AeoQueuedFill,
    AeoKnowledgeSource,
    KnowledgeAddResult,
    KnowledgeContent,
    KnowledgeLimits,
    KnowledgePage,
    StagedDeleteResult,
    AeoPrescreenRequest,
    AeoPrescreenResponse,
    AeoPublication,
    AeoTracker,
    AeoActivityCreate,
    AeoActivityEntry,
    AeoTrackerRebuild,
    AeoTrackerPatchBody,
    AeoTrackerSummary,
    AeoScanAudit,
    AeoScanAuditStatus,
    AeoTrackerTaskPatch,
    AeoActivitySummary,
    AeoSuggestionDecision,
    AeoPageContent,
    AeoPageDraft,
    AeoPageDraftRequest,
    AeoPagePublishResult,
    AeoPageHistoryChange,
    AeoPageHistoryEntry,
    AeoPageRole,
    AeoPageRoleOverride,
    AeoLinkGraph,
    AeoAnswersIndex,
    AeoQuestionAnswers,
} from "./types";

// A value, not a type: the one rule for "does this page still have the
// problem", so no surface re-implements it and drifts.
export { isFailingPage } from "./types";

export type {
    AeoAiVisibility,
    AeoAnchors,
    AeoSubmittedInputs,
    AeoAuditCheckPage,
    AeoAuditPageLocation,
    AeoAuditPageStatus,
    AeoDeadLink,
    AeoCrawledPage,
    AeoAnswerCoverage,
    AeoAreaScore,
    AeoCrawlAbsence,
    AeoVisibilityCoverage,
    AeoSourceDomain,
    AeoSourceKind,
    AeoScanVisibility,
    AeoLevelCap,
    AeoReadabilityAbsence,
    AeoReadabilityComponent,
    AeoCoverageStatus,
    AeoQuestionCorrected,
    AeoQuestionCorrection,
    AeoVerdictCorrection,
    AeoVerdictEngine,
    AeoVerdictReason,
    AeoAssessment,
    AeoAssessmentStatus,
    AeoAudit,
    AeoAuditCheck,
    AeoBusiness,
    AeoListStage,
    AeoStageCounts,
    AeoBuyMode,
    AeoLatestAttempt,
    AeoCompetitorWin,
    AeoCoverageGap,
    AeoDraft,
    AeoDraftCreateRequest,
    AeoReadabilityBlock,
    AeoReadabilityCrawl,
    AeoQueuedFill,
    AeoSearchMatch,
    AeoSearchQuestions,
    AeoSearchRow,
    AeoGoogleAiRefreshJob,
    AeoGoogleAiRefreshStart,
    AeoGoogleAiRefreshState,
    AeoRerunKind,
    AeoStageRerunJob,
    AeoStageRerunStart,
    AeoReadabilityRefreshJob,
    AeoReadabilityRefreshMode,
    AeoReadabilityRefreshStart,
    AeoKnowledgeSource,
    KnowledgeAddResult,
    KnowledgeContent,
    KnowledgeLimits,
    KnowledgePage,
    StagedDeleteResult,
    AeoProbe,
    AeoGoogleAiReading,
    AeoGoogleAiPending,
    AeoPublication,
    AeoSearchBuckets,
    AeoSearchKeyword,
    AeoMonthSummary,
    AeoTrafficInsights,
    AeoTrafficMarker,
    AeoSearchPage,
    AeoSearchPerformance,
    AeoSearchRollup,
    AeoSearchTotals,
    AeoSearchWindow,
    AeoSentiment,
    AeoFixRegisterRow,
    AeoScoreboardFactor,
    AeoTracker,
    AeoTrackerAffectedPage,
    AeoActivityCreate,
    AeoActivityEntry,
    AeoTrackerRebuild,
    AeoTrackerPatchBody,
    AeoTrackerSummary,
    AeoScanAudit,
    AeoScanAuditIssue,
    AeoScanAuditNote,
    AeoScanAuditStatus,
    AeoTrackerSummaryTask,
    AeoTrackerSuggestion,
    AeoSuggestionDecision,
    AeoActivitySummary,
    AeoTrackerTask,
    AeoTaskDoneBy,
    AeoTaskImpact,
    AeoTrackerTaskPatch,
    AeoTrackerWeek,
    AeoBusinessAudit,
    AeoBusinessCompetitor,
    AeoBusinessDetail,
    AeoScoreHistoryEntry,
    AeoBusinessSpend,
    AeoPagePublishable,
    AeoGrounding,
    AeoRunCost,
    AeoRunTokens,
    AeoAssessmentStage,
    AeoAssessmentEta,
    AeoBusinessDetailResult,
    AeoBusinessLatestAssessment,
    AeoCancelResult,
    AeoCompetitor,
    AeoConnector,
    AeoCountryShare,
    AeoEngineShare,
    AeoPageSpeed,
    AeoPageSpeedRun,
    AeoPsOpportunity,
    AeoQuery,
    AeoCreateAssessmentAccepted,
    AeoCreateAssessmentRequest,
    AeoCreateResult,
    AeoDetectRequest,
    AeoDetectedBusiness,
    AeoPrescreenRequest,
    AeoPrescreenResponse,
    AeoPrescreenVerdict,
    AeoProviderVisibility,
    AeoResults,
    AeoVisibility,
    PageKind,
    AeoPageBlock,
    AeoPageBlockLink,
    AeoPageContent,
    AeoPageDraft,
    AeoPageDraftRequest,
    AeoPagePublishResult,
    AeoPageHistoryChange,
    AeoPageHistoryEntry,
    AeoPageRecommendation,
    AeoPageRole,
    AeoPageRoleOverride,
    AeoPhraseMatch,
    AeoLinkGraph,
    AeoLinkGraphEdge,
    AeoVoiceProfile,
    AeoAnswerEngineSummary,
    AeoAnswerIndexEntry,
    AeoAnswersIndex,
    AeoQuestionAnswer,
    AeoQuestionAnswers,
} from "./types";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
// Mounted at the app root with prefix="/api/v1" — see app/interface/main.py.
const AEO_BASE = `${apiURL}/api/v1`;

/**
 * True while the AEO endpoints are not yet callable from the browser (the
 * X-AEO-Secret question in the header note). Every function below serves
 * fixtures in this mode so the Oura screens stay usable; flip
 * VITE_ENABLE_OURA_LIVE_API to "true" once ourteam-backend#3742 ships.
 */
const liveApiEnabled = import.meta.env.VITE_ENABLE_OURA_LIVE_API === "true";

/**
 * Whether these calls hit the real API or serve fixtures. Exported so callers
 * that must NOT run on invented numbers can refuse outright — the client report
 * renderer (#3494) prints a document the client receives, where a fixture is not
 * a developer convenience but a fabricated claim about their business.
 */
export const isOuraLiveApiEnabled = liveApiEnabled;

/**
 * How long to wait on POST .../connectors/{key}/connect.
 *
 * Generous, because the BE validates a WordPress application password by
 * calling the customer's site and following its redirects (20s per hop), and a
 * slow-but-working site should still connect. Bounded, because without a bound
 * the browser's own failure is indistinguishable from a backend that never
 * answers — which is the state this endpoint was reported in.
 */
const CONNECT_TIMEOUT_MS = 90_000;
const useFixtures = !liveApiEnabled;

// Say so once, loudly, outside dev — a silent fixture fallback on staging is
// exactly how "it looks like it works" turns into a wasted QA cycle.
if (useFixtures && !import.meta.env.DEV) {
    console.warn(
        "[aeo] serving FIXTURE data — VITE_ENABLE_OURA_LIVE_API is not \"true\". " +
        "Nothing on the Oura screens is real. See ourteam-backend#3742.",
    );
}

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    // Deliberately no X-AEO-Secret — see the auth note at the top of this file.
    // A browser cannot hold that secret, so the BE must accept this Bearer token
    // on the AEO routes (ourteam-backend#3742).
    return headers;
}

/**
 * The reason a call failed, in words the person who pressed the button can act on.
 *
 * The AEO API answers in TWO different error shapes and this layer only ever
 * read one of them:
 *   - `StandardResponse`  → `{ code, message, errors[] }`  (the routers' own
 *     4xx/5xx, e.g. "Application password rejected")
 *   - `HTTPException`     → `{ detail: string }`           (every auth/permission
 *     guard: 401 require_aeo_caller, 403 assert_can_access_domain)
 *   - validation          → `{ detail: [{ msg, loc }] }`   (422)
 *
 * Reading only `message` collapsed the last two — and a request that got NO
 * response at all — into a single "Please try again.", which is exactly why a
 * failing WordPress connect could not be diagnosed from the UI. Every branch
 * now says something different, and an unexplained failure still carries its
 * HTTP status so a screenshot is enough to tell 401 from 502.
 */
function readApiError(err: unknown, fallback: string): { message: string; code?: string } {
    const e = err as {
        code?: string;
        response?: {
            status?: number;
            data?: { message?: string; errors?: string[]; detail?: unknown };
        };
    };
    const res = e.response;

    // No response reached us: the request timed out, the network dropped, or a
    // proxy answered without CORS headers. Never "try again" — the WordPress
    // connect blocks on a live call out to the customer's own site, so this is
    // a real and expected outcome, not a blip.
    if (!res) {
        const timedOut = e.code === "ECONNABORTED" || e.code === "ETIMEDOUT";
        return {
            message: timedOut
                ? "The server did not answer in time. This usually means the site being checked is slow or unreachable from our servers."
                : "Could not reach the server. Check your connection and try again.",
            code: e.code,
        };
    }

    const data = res.data ?? {};
    if (data.message) return { message: data.message, code: data.errors?.[0] };

    // FastAPI's own shape. A string for HTTPException, an array for a 422.
    const { detail } = data;
    if (typeof detail === "string" && detail.trim()) return { message: detail };
    if (Array.isArray(detail)) {
        const parts = detail
            .map((d) => (d as { msg?: string })?.msg)
            .filter((m): m is string => Boolean(m));
        if (parts.length) return { message: parts.join("; ") };
    }

    // Nothing readable in the body — the status is still a fact worth showing,
    // because "HTTP 502" and "HTTP 403" send the reader to different people.
    return { message: res.status ? `${fallback} (HTTP ${res.status})` : fallback };
}

/** POST /api/v1/prescreen — sync qualifier (~5-15s). All engines down → 502, left to throw. */
export async function prescreen(input: AeoPrescreenRequest): Promise<AeoPrescreenResponse> {
    if (useFixtures) return devMock.prescreen(input);
    try {
        const res = await axios.post(`${AEO_BASE}/prescreen`, input, { headers: authHeaders() });
        return (res.data?.data ?? res.data) as AeoPrescreenResponse;
    } catch (err) {
        catchErrorLog(`[aeo] prescreen failed: ${err}`);
        throw err;
    }
}

/**
 * POST /api/v1/detect (ourteam-backend#3687) — crawl the domain and return
 * suggested business details for the New Scan "Details" step, so staff confirm
 * instead of type.
 *
 * Contractually always HTTP 200: an unreachable or low-signal site comes back
 * `confidence: "low"` with empty fields. A transport failure (network down,
 * 401 while ourteam-backend#3742 is open) is NOT a reason to block the flow —
 * we degrade to an empty low-confidence result so staff can fill the form in by
 * hand, which is exactly what the modal's "Prefilled what we could" copy
 * already covers. Never throws.
 */
export async function detect(domain: string): Promise<AeoDetectedBusiness> {
    if (useFixtures) return devMock.detect(domain);
    try {
        const res = await axios.post(
            `${AEO_BASE}/detect`,
            { domain },
            { headers: authHeaders() },
        );
        const data = (res.data?.data ?? res.data) as Partial<AeoDetectedBusiness> | undefined;
        // Normalize defensively — the form binds straight to these fields, so a
        // missing `anchors` or a null `services` must not crash the modal.
        return {
            business_name: data?.business_name ?? "",
            category: data?.category ?? "",
            anchors: {
                primary: data?.anchors?.primary ?? [],
                city: data?.anchors?.city ?? "",
            },
            services: data?.services ?? [],
            confidence: data?.confidence === "high" ? "high" : "low",
            // Only strings pass: the modal shows `reason` as written, and an
            // older backend sends neither field (ourteam-backend#5906).
            reason_code: typeof data?.reason_code === "string" ? data.reason_code : null,
            reason: typeof data?.reason === "string" ? data.reason : null,
        };
    } catch (err) {
        catchErrorLog(`[aeo] detect failed for ${domain}: ${err}`);
        return {
            business_name: "",
            category: "",
            anchors: { primary: [], city: "" },
            services: [],
            confidence: "low",
        };
    }
}

/** What POST /businesses/{id}/rescan answered. */
export type RescanResult =
    | { ok: true; assessmentId: string }
    /** 409: a scan for this site is already running. */
    | { ok: false; kind: "running"; assessmentId: string | null; message: string }
    /** Any other refusal, with the server's sentence when it sent one. */
    | { ok: false; kind: "refused"; code: string | null; message: string };

/**
 * POST /api/v1/businesses/{id}/rescan (BE aeo_business.py:269): asks the same
 * 50 again, seeded from the business's last scan. 202 `{assessment_id}`; 409
 * while a scan for the site runs; 422 `no_baseline` when nothing was scanned;
 * 403 `staff_only` for a client token once O11 ships. Never throws.
 *
 * Fixtures: a queued sample run for the site, which the scan job store then
 * picks up through `rehydrateScanJobs`.
 */
export async function rescanBusiness(businessId: string, sample?: { domain: string; businessName: string }): Promise<RescanResult> {
    if (useFixtures) {
        const domain = sample?.domain ?? businessId;
        const res = await devMock.createAssessment({
            domain,
            business_name: sample?.businessName ?? domain,
            anchors: { primary: ["Same 50"] },
            services: ["Same 50"],
        });
        if (res.ok) return { ok: true, assessmentId: res.data.assessment_id };
        if (res.kind === "duplicate") return { ok: false, kind: "running", assessmentId: res.assessment_id, message: "A scan for this site is already running." };
        return { ok: false, kind: "refused", code: null, message: "The rescan did not start." };
    }
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/rescan`, {}, { headers: authHeaders() });
        const data = (res.data?.data ?? res.data) as { assessment_id?: string } | undefined;
        if (data?.assessment_id) return { ok: true, assessmentId: data.assessment_id };
        return { ok: false, kind: "refused", code: null, message: "The rescan did not start." };
    } catch (err) {
        if (axios.isAxiosError(err)) {
            const status = err.response?.status;
            const body = err.response?.data as
                | { message?: string; detail?: string; errors?: string[]; data?: { assessment_id?: string } | null }
                | undefined;
            if (status === 409) {
                return { ok: false, kind: "running", assessmentId: body?.data?.assessment_id ?? null, message: "A scan for this site is already running." };
            }
            if (status && status >= 400 && status < 500) {
                const code = body?.errors?.[0] ?? null;
                return { ok: false, kind: "refused", code, message: body?.message || body?.detail || "The rescan was refused." };
            }
        }
        catchErrorLog(`[aeo] rescanBusiness failed for ${businessId}: ${err}`);
        return { ok: false, kind: "refused", code: null, message: "The rescan did not start. Try again." };
    }
}

/**
 * POST /api/v1/assessments — 202 + id, or the two expected non-2xx flow
 * branches (400 validation, 409 duplicate-in-flight for the domain).
 * Never throws for those two; anything else collapses to `kind: "error"`.
 */
export async function createAssessment(
    input: AeoCreateAssessmentRequest,
): Promise<AeoCreateResult> {
    if (useFixtures) return devMock.createAssessment(input);
    try {
        const res = await axios.post(`${AEO_BASE}/assessments`, input, { headers: authHeaders() });
        const data = (res.data?.data ?? res.data) as AeoCreateAssessmentAccepted;
        return { ok: true, data };
    } catch (err) {
        if (axios.isAxiosError(err)) {
            const status = err.response?.status;
            const body = err.response?.data as
                | { data?: { assessment_id?: string }; assessment_id?: string; errors?: string[] }
                | undefined;
            if (status === 409) {
                const existingId = body?.data?.assessment_id ?? body?.assessment_id;
                if (existingId) return { ok: false, kind: "duplicate", assessment_id: existingId };
            }
            if (status === 400) {
                return { ok: false, kind: "validation", errors: body?.errors ?? ["Validation failed"] };
            }
        }
        catchErrorLog(`[aeo] createAssessment failed: ${err}`);
        return { ok: false, kind: "error", message: "Could not start the assessment. Please try again." };
    }
}

/**
 * GET /api/v1/assessments/{id} — poll while queued|running; terminal states add results.
 *
 * `renderToken` (#3494, BE ourteam-backend#3839) is the short-lived credential the
 * PDF renderer carries instead of a session: headless Chromium printing the client
 * report has no cookie. It is sent as `X-AEO-Render-Token` rather than the
 * `render_token` query param the BE also accepts — a header keeps the credential
 * out of access logs and referrers. The BE scopes it to exactly this call for
 * exactly this assessment id (`require_aeo_caller` → `verify_render_token`), so it
 * is not a general API key; a token minted for another assessment is refused there,
 * not here.
 *
 * When a render token is present the session Bearer is deliberately NOT sent: the
 * renderer has none, and mixing the two would hide a broken token behind whatever
 * cookie a developer happened to have while testing the route.
 */
export async function getAssessment(id: string, renderToken?: string): Promise<AeoAssessment> {
    if (useFixtures) return devMock.getAssessment(id);
    try {
        const headers = renderToken
            ? { "Content-Type": "application/json", "X-AEO-Render-Token": renderToken }
            : authHeaders();
        const res = await axios.get(`${AEO_BASE}/assessments/${id}`, { headers });
        return (res.data?.data ?? res.data) as AeoAssessment;
    } catch (err) {
        catchErrorLog(`[aeo] getAssessment failed: ${err}`);
        throw err;
    }
}

/**
 * GET /api/v1/assessments/{id}/answers — the index of stored answers
 * (ourteam-backend, `feature/oura-variable-kinds-staging`; FE #3555).
 *
 * Deliberately NO answer bodies: `results.probes[].answer` is trimmed to 600
 * chars because it rides along on every poll, and shipping 50 full answers is
 * ~300KB of prose almost nobody opens. Render the list from this, then fetch a
 * body only when a reader opens one.
 *
 * `stored: false` comes back as a 200 — a run from before answers were captured
 * is a different finding from "the engines said nothing", and the caller must be
 * able to tell them apart.
 */
export async function getAssessmentAnswersIndex(
    id: string,
    /** Which instrument's answers to list — `chatgpt_app` for the real app
     *  (#3564). Omitted lets the backend choose, which is what an older one
     *  did anyway. */
    instrument?: string,
): Promise<AeoAnswersIndex> {
    const empty: AeoAnswersIndex = {
        assessment_id: id,
        stored: false,
        reason: "answers were not captured on this run",
        questions: [],
    };
    // Fixtures have no answer store; say so honestly rather than inventing prose.
    if (useFixtures) return empty;
    try {
        const res = await axios.get(`${AEO_BASE}/assessments/${id}/answers`, {
            headers: authHeaders(),
            ...(instrument ? { params: { instrument } } : {}),
        });
        const data = (res.data?.data ?? res.data) as AeoAnswersIndex | undefined;
        if (!data) return empty;
        return { ...data, questions: data.questions ?? [] };
    } catch (err) {
        catchErrorLog(`[aeo] getAssessmentAnswersIndex failed: ${err}`);
        throw err;
    }
}

/**
 * GET /api/v1/assessments/{id}/answers?question=… — every engine's full answer
 * to ONE question.
 *
 * Matched on the question TEXT (the BE normalises it), never on an index: a
 * position silently re-points when a question set is re-authored, and the reader
 * would then be shown the wrong answer under the right question.
 *
 * Returns `null` when the BE answers 404 `question_not_found` — a stale or
 * mistyped question, which the caller shows as "not on this scan" rather than as
 * a failure.
 */
export async function getAssessmentAnswer(
    id: string,
    /** The index's `n`, or the full question text — the backend takes either.
     *  The number is preferred: it survives a question whose wording contains
     *  characters a query string would have to carry verbatim. */
    question: string | number,
    instrument?: string,
): Promise<AeoQuestionAnswers | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.get(`${AEO_BASE}/assessments/${id}/answers`, {
            headers: authHeaders(),
            params: { question, ...(instrument ? { instrument } : {}) },
        });
        const data = (res.data?.data ?? res.data) as AeoQuestionAnswers | undefined;
        if (!data) return null;
        return { ...data, answers: data.answers ?? [] };
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) return null;
        catchErrorLog(`[aeo] getAssessmentAnswer failed: ${err}`);
        throw err;
    }
}

/** GET /api/v1/assessments?domain=&limit= — history for re-runs / month-over-month, newest first. */
/**
 * POST /api/v1/assessments/{id}/cancel (ourteam-backend#3982) — stop a run.
 *
 * Cooperative, not a kill: the BE flags the row terminal and the worker checks
 * that flag at each stage boundary. So the stage already in flight finishes and
 * is still paid for; every stage after it is not. The property that matters
 * operationally is that the domain is released IMMEDIATELY — the duplicate
 * guard only matches `queued`/`running`, so a corrected scan can start in the
 * same second rather than waiting out the wrong one.
 *
 * A 409 means the run reached a terminal state first. That is not a failure to
 * report: the caller drops the cancel and lets the next poll show the real
 * outcome.
 */
export async function cancelAssessment(id: string): Promise<AeoCancelResult> {
    if (useFixtures) return devMock.cancelAssessment(id);
    try {
        const res = await axios.post(
            `${AEO_BASE}/assessments/${id}/cancel`,
            {},
            { headers: authHeaders() },
        );
        const data = (res.data?.data ?? res.data) as {
            status?: AeoAssessmentStatus;
            stage?: string | null;
            cancelled_at?: string | null;
        };
        return {
            ok: true,
            // Trust the BE's own word on the resulting state rather than
            // assuming "cancelled" — it owns the status vocabulary.
            status: data?.status ?? "cancelled",
            stage: data?.stage ?? null,
            cancelled_at: data?.cancelled_at ?? null,
        };
    } catch (err) {
        if (axios.isAxiosError(err)) {
            const status = err.response?.status;
            // Expected outcomes, not incidents — deliberately not sent to Sentry.
            if (status === 409) return { ok: false, kind: "already_terminal" };
            if (status === 404) return { ok: false, kind: "not_found" };
        }
        catchErrorLog(`[aeo] cancelAssessment failed for ${id}: ${err}`);
        return { ok: false, kind: "error" };
    }
}

export async function listAssessments(domain: string, limit = 20): Promise<AeoAssessment[]> {
    if (useFixtures) return devMock.listAssessments(domain, limit);
    try {
        const res = await axios.get(`${AEO_BASE}/assessments`, {
            headers: authHeaders(),
            params: { domain, limit },
        });
        const body = res.data?.data ?? res.data;
        return Array.isArray(body) ? body : (body?.assessments ?? []);
    } catch (err) {
        catchErrorLog(`[aeo] listAssessments failed: ${err}`);
        return [];
    }
}

/**
 * GET /api/v1/assessments with NO domain (ourteam-backend#3793) — the caller's
 * assessments across every domain, newest first, scoped exactly like
 * /businesses (staff see the workspace, a client sees only their own).
 *
 * Used to rehydrate in-flight scans after a page reload, which was impossible
 * while `domain` was a required param. Never throws — a failure degrades to an
 * empty list, matching the sibling readers.
 */
export async function listAllAssessments(limit = 50): Promise<AeoAssessment[]> {
    if (useFixtures) return devMock.listAllAssessments(limit);
    try {
        const res = await axios.get(`${AEO_BASE}/assessments`, {
            headers: authHeaders(),
            params: { limit },
        });
        const body = res.data?.data ?? res.data;
        return Array.isArray(body) ? body : (body?.assessments ?? []);
    } catch (err) {
        catchErrorLog(`[aeo] listAllAssessments failed: ${err}`);
        return [];
    }
}

/**
 * DELETE /api/v1/businesses/{id} (ourteam-backend#3793) — remove a business
 * from the pipeline. **Soft delete** BE-side: the row is marked, not destroyed
 * (its assessments cost real provider money), and a re-scan of the same domain
 * revives it. Staff only — a client caller gets 403.
 *
 * Returns true on success, false on any failure, so the caller can show a
 * snackbar without unpacking an error shape.
 */
export async function deleteBusiness(id: string): Promise<boolean> {
    if (useFixtures) return devMock.deleteBusiness(id);
    try {
        await axios.delete(`${AEO_BASE}/businesses/${id}`, { headers: authHeaders() });
        return true;
    } catch (err) {
        catchErrorLog(`[aeo] deleteBusiness failed for ${id}: ${err}`);
        return false;
    }
}

/**
 * GET /api/v1/businesses (ourteam-backend#3747) — the staff pipeline list.
 * Each row carries enough of its newest terminal assessment
 * (`latest_assessment`) to render the Businesses table without a per-row
 * fetch. `latest_assessment` is `null` only when the business genuinely has
 * no terminal run yet — never inferred as zero.
 */
export async function listBusinesses(): Promise<AeoBusiness[]> {
    return (await listBusinessesWithCounts()).businesses;
}

/**
 * The same list, with the server's tab counts (ourteam-backend#6091).
 * `stageCounts` is null when the server sends none (an older server, the
 * fixtures, or a failed read): the caller counts its own rows then.
 */
export async function listBusinessesWithCounts(): Promise<{ businesses: AeoBusiness[]; stageCounts: AeoStageCounts | null }> {
    if (useFixtures) return { businesses: await devMock.listBusinesses(), stageCounts: null };
    try {
        const res = await axios.get(`${AEO_BASE}/businesses`, { headers: authHeaders() });
        const body = res.data?.data ?? res.data;
        const businesses: AeoBusiness[] = Array.isArray(body?.businesses) ? body.businesses : [];
        return { businesses, stageCounts: stageCountsOf(body?.stageCounts) };
    } catch (err) {
        catchErrorLog(`[aeo] listBusinesses failed: ${err}`);
        return { businesses: [], stageCounts: null };
    }
}

const STAGE_COUNT_KEYS: readonly (AeoListStage | "all")[] = ["active", "onboarding", "prospect", "ended", "all"];

/** Every key a number, or null: a half-sent block is not a count to show. */
function stageCountsOf(raw: unknown): AeoStageCounts | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    if (!STAGE_COUNT_KEYS.every((k) => typeof r[k] === "number")) return null;
    return { active: r.active as number, onboarding: r.onboarding as number, prospect: r.prospect as number,
        ended: r.ended as number, all: r.all as number };
}

/** The read-only session `POST /businesses/{id}/view-as` opens (ourteam-backend#5823). */
export interface ViewAsMerchantSession {
    accessToken: string;
    /** The merchant's own subdomain; the session opens there, in a new tab. */
    tokenDomain: string;
    companyId: string | null;
    /**
     * True while the owner behind the session is the on-demand placeholder, i.e.
     * the merchant has no login yet. `null` when the server did not say: read it
     * as unknown, never as false.
     */
    placeholder: boolean | null;
    expiresInSeconds: number | null;
}

export type ViewAsMerchantResult =
    | { ok: true; session: ViewAsMerchantSession }
    | { ok: false; message: string };

/**
 * POST /api/v1/businesses/{id}/view-as (ourteam-backend#5823).
 *
 * Opens the merchant's workspace for staff WITHOUT an access login: the server
 * creates the workspace and a placeholder owner on first use and answers with
 * the same read-only view-as token `/auth/switch-workspace` mints. A failure
 * (403 not staff, 404 unknown business, or anything else) creates nothing, so
 * the caller can press again; the message is the server's own sentence.
 *
 * No fixture answer: a token for an invented workspace would be a lie, so
 * callers hide the control while the live API is off (`isOuraLiveApiEnabled`).
 */
function isTrustedTokenDomain(value: unknown): value is string {
    if (typeof value !== "string" || value.length === 0) return false;
    try {
        const url = new URL(`https://${value}`);
        const host = url.hostname.toLowerCase();
        return (
            url.username === "" &&
            url.password === "" &&
            url.port === "" &&
            url.pathname === "/" &&
            url.search === "" &&
            url.hash === "" &&
            (host === "ourteam.ai" || host.endsWith(".ourteam.ai"))
        );
    } catch {
        return false;
    }
}

export async function viewAsMerchant(businessId: string): Promise<ViewAsMerchantResult> {
    const unavailable = "Could not open the merchant's screens. Please try again.";
    if (useFixtures) return { ok: false, message: "View as the merchant needs the live API." };
    try {
        const res = await axios.post(
            `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/view-as`,
            undefined,
            { headers: authHeaders(), timeout: 30_000 },
        );
        const body = res.data;
        const data = body?.data;
        // A StandardResponse 200 envelope can still carry a 403/404 code.
        if (typeof body?.code === "number" && body.code !== 200) {
            return { ok: false, message: body.message || unavailable };
        }
        // The session token goes into this host's URL, so only a plain OurTeam
        // host is accepted: no userinfo, port, path or foreign domain.
        if (!data?.access_token || !isTrustedTokenDomain(data?.token_domain)) {
            return { ok: false, message: body?.message || unavailable };
        }
        return {
            ok: true,
            session: {
                accessToken: data.access_token,
                tokenDomain: data.token_domain,
                companyId: data.company_id ?? null,
                placeholder: typeof data.placeholder === "boolean" ? data.placeholder : null,
                expiresInSeconds: typeof data.expires_in_seconds === "number" ? data.expires_in_seconds : null,
            },
        };
    } catch (err) {
        catchErrorLog(`[aeo] viewAsMerchant failed for ${businessId}: ${err}`);
        return { ok: false, message: readApiError(err, unavailable).message };
    }
}

/**
 * POST /api/v1/businesses/{id}/readability/refresh (ourteam-backend#4995).
 *
 * The AI Readability score is a pure function of the crawl, so moving it does
 * not need an assessment — staff were running a whole scan, engine probes and
 * all, to see five numbers that no engine produces.
 *
 * Three refusals worth telling apart, because they need three different things
 * from the operator: a 409 means a scan is already running and the answer is to
 * wait, a 404 means this deployment does not have the route and the answer is
 * to stop offering the control, and anything else is a failure to say out loud.
 */
export async function startReadabilityRefresh(businessId: string): Promise<AeoReadabilityRefreshStart> {
    if (useFixtures) return devMock.startReadabilityRefresh(businessId);
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${businessId}/readability/refresh`, null, {
            headers: authHeaders(),
        });
        const body = (res.data?.data ?? res.data) as {
            jobId?: string;
            mode?: AeoReadabilityRefreshMode;
            assessmentId?: string | null;
            aiReadability?: AeoReadabilityBlock | null;
        };
        if (!body?.jobId) return { ok: false, kind: "error", message: "The refresh did not start." };
        return {
            ok: true,
            jobId: body.jobId,
            mode: body.mode ?? "crawl",
            assessmentId: body.assessmentId ?? null,
            aiReadability: body.aiReadability ?? null,
        };
    } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) return { ok: false, kind: "absent" };
        const { message } = readApiError(err, "The refresh could not be started.");
        if (status === 409) {
            const data = (err as { response?: { data?: { data?: { assessmentId?: string } } } }).response?.data?.data;
            return { ok: false, kind: "busy", message, assessmentId: data?.assessmentId ?? null };
        }
        catchErrorLog(`[aeo] startReadabilityRefresh failed for ${businessId}: ${err}`);
        return { ok: false, kind: "error", message };
    }
}

/**
 * The slot fills queued for the next scan (ourteam-backend#4982).
 *
 * A queued fill rebinds ONE variable in the question recipe at the next scan —
 * the set stays 50 and the slot keeps its number, group and check. At most five
 * are held at a time, which the server enforces with a 409.
 */
export async function getQueuedFills(businessId: string): Promise<AeoQueuedFill[]> {
    if (useFixtures) return devMock.getQueuedFills(businessId);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/question-fills`, {
            headers: authHeaders(),
        });
        const body = (res.data?.data ?? res.data) as { queued?: AeoQueuedFill[] } | AeoQueuedFill[] | null;
        if (Array.isArray(body)) return body;
        return body?.queued ?? [];
    } catch (err) {
        catchErrorLog(`[aeo] getQueuedFills failed for ${businessId}: ${err}`);
        return [];
    }
}

/**
 * Queue one.
 *
 * Four answers the caller has to tell apart: it worked, five are already
 * queued (409), a client tried to write (403), and anything else. A screen
 * that collapses them shows "could not add" to an operator whose only real
 * problem is that the queue is full.
 */
export async function queueQuestionFill(
    businessId: string,
    fill: { variable: string; value: string; replaces?: string | null },
): Promise<{ ok: true } | { ok: false; kind: "full" | "forbidden" | "absent" | "error"; message: string }> {
    if (useFixtures) return devMock.queueQuestionFill(businessId, fill);
    try {
        await axios.post(`${AEO_BASE}/businesses/${businessId}/question-fills`, fill, {
            headers: authHeaders(),
        });
        return { ok: true };
    } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        const { message } = readApiError(err, "That search could not be queued.");
        if (status === 409) return { ok: false, kind: "full", message };
        if (status === 403) return { ok: false, kind: "forbidden", message };
        if (status === 404 || status === 405) return { ok: false, kind: "absent", message };
        catchErrorLog(`[aeo] queueQuestionFill failed for ${businessId}: ${err}`);
        return { ok: false, kind: "error", message };
    }
}

/** Take one back off the queue. Keyed on the variable, as the route is. */
export async function unqueueQuestionFill(businessId: string, variable: string): Promise<boolean> {
    if (useFixtures) return devMock.unqueueQuestionFill(businessId, variable);
    try {
        await axios.delete(
            `${AEO_BASE}/businesses/${businessId}/question-fills/${encodeURIComponent(variable)}`,
            { headers: authHeaders() },
        );
        return true;
    } catch (err) {
        catchErrorLog(`[aeo] unqueueQuestionFill failed for ${variable}: ${err}`);
        return false;
    }
}

/**
 * PATCH /api/v1/businesses/{id} — the display name (ourteam-backend#5003).
 *
 * The name a business was created with came from whatever the scan form said,
 * and the detector is often close rather than right. This changes the label
 * only: the DOMAIN is the key every other table stores, so renaming one is a
 * migration and not in this call.
 *
 * `absent` is a 404 or a 405 on a business that exists — the route is not
 * deployed here, and FastAPI answers 405 when the path exists for another
 * method, which is exactly today's state. The caller hides the control rather
 * than offering an edit that cannot be saved.
 */
/**
 * Rename a business, and set its city with the same PATCH (#3975).
 *
 * `city` is sent ONLY when it changed. The route takes it on staging today but
 * not on production (`BusinessRenameIn` has no `city` there yet,
 * ourteam-backend#5257), and a field an older server does not know is dropped
 * in silence — so the caller is told what the server answered with, and a
 * rename on its own behaves exactly as it did before.
 *
 * `cityKept` is what the server holds now: when a city was sent and comes back
 * unchanged, the route ignored it, and that is reported rather than shown as a
 * save that worked.
 */
export async function renameBusiness(
    businessId: string,
    name: string,
    city?: string,
): Promise<{ ok: true; cityKept?: string | null } | { ok: false; kind: "absent" | "error"; message: string }> {
    if (useFixtures) return devMock.renameBusiness(businessId, name);
    try {
        const r = await axios.patch(
            `${AEO_BASE}/businesses/${businessId}`,
            { business_name: name, ...(city === undefined ? {} : { city }) },
            { headers: authHeaders() },
        );
        const body = (r.data as { data?: { city?: unknown } } | null)?.data;
        const kept = typeof body?.city === "string" ? body.city : null;
        return { ok: true, cityKept: body && "city" in body ? kept : undefined };
    } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        const { message } = readApiError(err, "That name could not be saved.");
        if (status === 404 || status === 405) return { ok: false, kind: "absent", message };
        catchErrorLog(`[aeo] renameBusiness failed for ${businessId}: ${err}`);
        return { ok: false, kind: "error", message };
    }
}

/**
 * GET the job.
 *
 * Three answers, and telling them apart is the whole point: the job, `"gone"`
 * for a 404 (the id expired or never existed — the caller stops and forgets
 * it), and `null` for a poll that failed to ask. One dropped request during a
 * crawl must not report the crawl as broken, so `null` means keep polling.
 */
export async function getReadabilityRefresh(
    businessId: string,
    jobId: string,
): Promise<AeoReadabilityRefreshJob | "gone" | null> {
    if (useFixtures) return devMock.getReadabilityRefresh(businessId, jobId);
    try {
        const res = await axios.get(
            `${AEO_BASE}/businesses/${businessId}/readability/refresh/${jobId}`,
            { headers: authHeaders() },
        );
        return (res.data?.data ?? res.data) as AeoReadabilityRefreshJob;
    } catch (err) {
        if ((err as { response?: { status?: number } }).response?.status === 404) return "gone";
        catchErrorLog(`[aeo] getReadabilityRefresh failed for ${jobId}: ${err}`);
        return null;
    }
}

/**
 * GET /api/v1/businesses/{id} (ourteam-backend#3747) — one business plus its
 * latest assessment in full (ourteam-frontend#3160). `id` accepts the business
 * UUID or its domain, matching the shipped connectors route. `null` on a
 * genuine 404 (bad id) — the caller renders a "not found" state, never a
 * silent redirect.
 */
export async function getBusinessDetail(id: string): Promise<AeoBusinessDetailResult> {
    if (useFixtures) {
        const detail = await devMock.getBusinessDetail(id);
        return detail ? { ok: true, detail } : { ok: false, kind: "notFound" };
    }
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${id}`, { headers: authHeaders() });
        return { ok: true, detail: (res.data?.data ?? res.data) as AeoBusinessDetail };
    } catch (err) {
        // ONLY a 404 means "this business does not exist". Everything else — a
        // 5xx, a dropped connection, an expired session — is a failure to ask,
        // and the caller must be able to tell the difference: it redirects away
        // on notFound, and that redirect made real businesses look deleted.
        if (axios.isAxiosError(err) && err.response?.status === 404) {
            return { ok: false, kind: "notFound" };
        }
        catchErrorLog(`[aeo] getBusinessDetail failed for ${id}: ${err}`);
        return { ok: false, kind: "error" };
    }
}

/**
 * GET /api/v1/businesses/{id}/score-history (ourteam-backend#4572) — the AEO/SEO
 * score series for one business, newest first, one lightweight row per terminal
 * assessment.
 *
 * Deliberately NOT listAssessments(domain): that returns the full `results` blob
 * per row (crawl checks, per-page audit, AI visibility), which is why this
 * endpoint exists at all.
 *
 * Returns [] on failure rather than throwing — the History tab renders its own
 * empty state, and a chart that fails to load must not take the tab down with it.
 */
export async function getScoreHistory(businessId: string, limit = 50): Promise<AeoScoreHistoryEntry[]> {
    return (await readScoreHistory(businessId, limit)) ?? [];
}

/**
 * The same read, but null when it fails, for a caller that must tell "no earlier
 * scan" from "could not read the history" (Ask Oura says "only one scan" from an
 * empty list, so a failed read must not look like one).
 */
export async function readScoreHistory(businessId: string, limit = 50): Promise<AeoScoreHistoryEntry[] | null> {
    if (useFixtures) return devMock.getScoreHistory(businessId).slice(0, limit);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/score-history`, {
            headers: authHeaders(),
            params: { limit },
        });
        const body = res.data?.data ?? res.data;
        return Array.isArray(body?.history) ? (body.history as AeoScoreHistoryEntry[]) : [];
    } catch (err) {
        catchErrorLog(`[aeo] getScoreHistory failed for ${businessId}: ${err}`);
        return null;
    }
}

/**
 * GET /api/v1/businesses/{id}/spend (ourteam-backend#6689): what is recorded as
 * spent on one client, all time per source and week by week. Staff only. Null
 * on failure: the card says it could not be read rather than showing $0.
 */
export async function getBusinessSpend(businessId: string, weeks = 12): Promise<AeoBusinessSpend | null> {
    if (useFixtures) return devMock.getBusinessSpend(businessId, weeks);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/spend`, { headers: authHeaders(), params: { weeks } });
        const body = (res.data?.data ?? res.data) as AeoBusinessSpend | undefined;
        return body && Array.isArray(body.weeks) && Array.isArray(body.sources) ? body : null;
    } catch (err) {
        catchErrorLog(`[aeo] getBusinessSpend failed for ${businessId}: ${err}`);
        return null;
    }
}

export type VersionPublishResult = { ok: true } | { ok: false; code: string; message: string };

/**
 * Publishing or hiding a whole question version is one backend call (handoff:
 * oura-version-publish), so a version is never left half shown. Until that
 * route is live the version switch is offered on sample businesses only.
 */
export const VERSION_PUBLISH_LIVE = import.meta.env.VITE_ENABLE_OURA_VERSION_PUBLISH_LIVE === "true";

const publishRefusal = (err: unknown, fallback: string): { ok: false; code: string; message: string } => {
    const code = axios.isAxiosError(err) ? (err.response?.data?.errors?.[0] as string | undefined) : undefined;
    const message = code === "first_results_not_sent"
        ? "The first check goes to the client from Onboarding, with the results and plan."
        : code === "last_published"
            ? "The client must keep at least one version to see."
            : fallback;
    return { ok: false, code: code ?? "error", message };
};

/**
 * POST /api/v1/businesses/{id}/question-sets/{set}/publication (backend
 * handoff, 22 Oct 2026): publish or hide every finished scan of one question
 * version, in one transaction. `assessmentId` is any scan of the version: the
 * server finds the whole version from it (its set, between trend breaks), so
 * scans older than the history this page loaded are included. `notify` tells
 * the client about the newest scan only, once.
 */
export async function setQuestionVersionPublished(input: {
    businessId: string;
    questionSetId: string;
    assessmentId: string;
    published: boolean;
    notify: boolean;
}): Promise<VersionPublishResult> {
    if (!VERSION_PUBLISH_LIVE) return { ok: false, code: "not_available", message: "Publishing a version needs a backend update." };
    try {
        await axios.post(
            `${AEO_BASE}/businesses/${encodeURIComponent(input.businessId)}/question-sets/${encodeURIComponent(input.questionSetId)}/publication`,
            { published: input.published, notify: input.notify, assessment_id: input.assessmentId },
            { headers: authHeaders() },
        );
        return { ok: true };
    } catch (err) {
        catchErrorLog(`[aeo] setQuestionVersionPublished failed for ${input.questionSetId}: ${err}`);
        return publishRefusal(err, input.published ? "It did not publish. Try again." : "It was not hidden. Try again.");
    }
}

/** fixture-only: publish or hide a sample business's scans on this preview. */
export function setFixtureScansPublished(assessmentIds: string[], published: boolean): Promise<VersionPublishResult> {
    return devMock.setScansPublished(assessmentIds, published);
}

export type CorrectQuestionResult =
    | { ok: true; was: string; text: string }
    | { ok: false; code: string; message: string };

/** Sample data: corrections "asked" after a short wait, kept for the tab. */
const sampleCorrections: Record<string, Record<string, AeoQuestionCorrection>> = {};

/**
 * POST /api/v1/assessments/{id}/questions/{n}/correct {text} (backend commit 25ad7f07):
 * staff put new words on ONE question of a finished scan, its old answers are cleared, and
 * that question alone is asked again in the background. `dryRun` changes nothing and costs
 * nothing: it says whether the correction would be refused. The live outcome is
 * `results.questionCorrections[n]` (getQuestionCorrections). One at a time per scan: a second
 * while one is `asking` is refused `ask_in_progress`.
 */
export async function correctQuestion(assessmentId: string, n: number, text: string, opts?: { dryRun?: boolean }): Promise<CorrectQuestionResult> {
    if (useFixtures) {
        if (!opts?.dryRun) {
            const map = (sampleCorrections[assessmentId] ??= {});
            map[String(n)] = { state: "asking", text, version: ((map[String(n)]?.version ?? 1) + 1) };
            window.setTimeout(() => {
                map[String(n)] = { ...map[String(n)], state: "asked" };
            }, 2500);
        }
        return { ok: true, was: "", text };
    }
    try {
        const res = await axios.post(
            `${AEO_BASE}/assessments/${encodeURIComponent(assessmentId)}/questions/${n}/correct${opts?.dryRun ? "?dry_run=true" : ""}`,
            { text },
            { headers: authHeaders() },
        );
        const data = (res.data?.data ?? res.data) as { was?: string; text?: string } | undefined;
        return { ok: true, was: data?.was ?? "", text: data?.text ?? text };
    } catch (err) {
        const r = (err as { response?: { data?: { message?: string; errors?: string[] } } }).response?.data;
        catchErrorLog(`[aeo] correctQuestion failed for ${assessmentId} Q${n}: ${err}`);
        return { ok: false, code: r?.errors?.[0] ?? "network", message: r?.message ?? "It did not go through. Try again." };
    }
}

/**
 * Correcting one engine's verdict on one question (handoff: oura-top10, 22 Oct
 * 2026). Until the route is live it is offered on sample businesses only.
 */
export const VERDICT_FIX_LIVE = import.meta.env.VITE_ENABLE_OURA_VERDICT_FIX_LIVE === "true";

export type VerdictFixResult = { ok: true } | { ok: false; code: string; message: string };

/**
 * POST /api/v1/assessments/{id}/questions/{n}/verdict {engine, named, reason, note}:
 * staff set Named or Not named for one engine on one question of the latest
 * check. Nothing is asked again; the server recounts the score with it and
 * keeps who, when and why at `results.verdictCorrections[n][engine]`. DELETE on
 * the same path with `?engine=` takes it back. Samples answer at once.
 */
export async function correctVerdict(input: {
    assessmentId: string;
    n: number;
    engine: AeoVerdictEngine;
    named: boolean;
    reason: AeoVerdictReason;
    note?: string;
    sample: boolean;
}): Promise<VerdictFixResult> {
    if (input.sample) return { ok: true };
    if (!VERDICT_FIX_LIVE) return { ok: false, code: "not_available", message: "Correcting a verdict needs a backend update." };
    try {
        await axios.post(
            `${AEO_BASE}/assessments/${encodeURIComponent(input.assessmentId)}/questions/${input.n}/verdict`,
            { engine: input.engine, named: input.named, reason: input.reason, note: input.note || null },
            { headers: authHeaders() },
        );
        return { ok: true };
    } catch (err) {
        const r = (err as { response?: { data?: { message?: string; errors?: string[] } } }).response?.data;
        catchErrorLog(`[aeo] correctVerdict failed for ${input.assessmentId} Q${input.n}: ${err}`);
        return { ok: false, code: r?.errors?.[0] ?? "network", message: r?.message ?? "It was not saved. Try again." };
    }
}

export async function undoVerdict(input: { assessmentId: string; n: number; engine: AeoVerdictEngine; sample: boolean }): Promise<VerdictFixResult> {
    if (input.sample) return { ok: true };
    if (!VERDICT_FIX_LIVE) return { ok: false, code: "not_available", message: "Correcting a verdict needs a backend update." };
    try {
        await axios.delete(
            `${AEO_BASE}/assessments/${encodeURIComponent(input.assessmentId)}/questions/${input.n}/verdict?engine=${input.engine}`,
            { headers: authHeaders() },
        );
        return { ok: true };
    } catch (err) {
        catchErrorLog(`[aeo] undoVerdict failed for ${input.assessmentId} Q${input.n}: ${err}`);
        return { ok: false, code: "network", message: "It was not undone. Try again." };
    }
}

/** The live state of every correction on a scan (results.questionCorrections). */
export async function getQuestionCorrections(assessmentId: string): Promise<Record<string, AeoQuestionCorrection>> {
    if (useFixtures) return { ...(sampleCorrections[assessmentId] ?? {}) };
    const a = await getAssessment(assessmentId);
    return (a.results as { questionCorrections?: Record<string, AeoQuestionCorrection> } | null | undefined)?.questionCorrections ?? {};
}

/** GET /api/v1/businesses/{id}/connectors (#3685) — the data-source connector catalog for one business. */
export async function getBusinessConnectors(id: string): Promise<AeoConnector[]> {
    if (useFixtures) return devMock.getBusinessConnectors(id);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${id}/connectors`, { headers: authHeaders() });
        const body = res.data?.data ?? res.data;
        return Array.isArray(body) ? body : [];
    } catch (err) {
        catchErrorLog(`[aeo] getBusinessConnectors failed for ${id}: ${err}`);
        return [];
    }
}

/**
 * What a connector needs to authenticate.
 *
 * Google connectors (`gsc`, `ga4`) take no credentials — the BE answers with a
 * consent URL to send the browser to. WordPress and Framer are validated live
 * against the target during this call, so a wrong password fails here rather
 * than silently at publish time.
 */
export interface AeoConnectPayload {
    /** WordPress */
    site_url?: string;
    username?: string;
    application_password?: string;
    /** Framer */
    project_url?: string;
    api_key?: string;
    /** Wix (ourteam-backend#4758). `api_key` above is shared with Framer. */
    account_id?: string;
    /**
     * Only sent on the SECOND attempt, after the account turned out to hold
     * more than one site matching this domain and a person picked one. Absent
     * on a first connect: the server resolves the domain itself, and sending a
     * guess here would connect the wrong site silently.
     */
    site_id?: string;
}

/**
 * One site the Wix account holds that matches the business domain, offered when
 * more than one does (`wix_multiple_matching_sites`).
 *
 * **Field names are a PROPOSAL, not yet a contract.** ourteam-backend#4758 says
 * the error comes back "with the candidates" and stops there, so these borrow
 * the names that ticket already commits to for `metadata_json` — `site_id` and
 * `site_display_name` — on the reasoning that the same thing should not be
 * called two things on one wire. Flagged on #4758 and #3691. Everything that
 * reads this is null-safe: an unrecognised shape yields no candidates and the
 * modal says so, rather than rendering a picker of blanks.
 */
export interface WixSiteCandidate {
    site_id: string;
    site_display_name?: string;
    domain?: string;
}

export type ConnectResult =
    /** Google: send the browser here to finish consent. */
    | { ok: true; kind: "redirect"; url: string }
    /** WordPress / Framer: credentials accepted and stored. */
    | { ok: true; kind: "connected"; detail: string }
    /** The BE's own message — "WordPress rejected the credentials" is what the
     *  user needs, not "something went wrong". */
    | { ok: false; message: string; code?: string; sites?: WixSiteCandidate[] };

/**
 * The sites a `wix_multiple_matching_sites` failure offers to choose between.
 *
 * Deliberately forgiving about where they sit — `data.sites` or `sites` — and
 * unforgiving about their shape: a candidate without a `site_id` cannot be
 * submitted, so it is dropped rather than rendered as a row that does nothing.
 * An empty result is the honest answer when the server sends something this
 * does not recognise, and the modal has copy for exactly that case.
 */
function readWixCandidates(err: unknown): WixSiteCandidate[] | undefined {
    const body = (err as { response?: { data?: Record<string, unknown> } }).response?.data;
    const inner = (body?.data ?? body) as Record<string, unknown> | undefined;
    const raw = inner?.sites;
    if (!Array.isArray(raw)) return undefined;
    const sites = raw
        .filter((s): s is Record<string, unknown> => !!s && typeof s === "object")
        .map((s) => ({
            site_id: String(s.site_id ?? ""),
            site_display_name: s.site_display_name ? String(s.site_display_name) : undefined,
            domain: s.domain ? String(s.domain) : undefined,
        }))
        .filter((s) => s.site_id);
    return sites.length > 0 ? sites : undefined;
}

/**
 * POST /api/v1/businesses/{id}/connectors/{key}/connect (ourteam-backend#3685).
 *
 * Two different shapes behind one endpoint, distinguished by what comes back:
 * a `redirect_url` for the Google connectors, a stored connection for the
 * credential-based ones.
 */
export async function connectConnector(
    businessId: string,
    key: string,
    payload: AeoConnectPayload = {},
): Promise<ConnectResult> {
    // Fixtures cannot reach a client's account, but the Wix form's three
    // interesting outcomes — connected, missing scope, two matching sites —
    // are otherwise unreachable without a real Wix account to fail against.
    if (useFixtures) return devMock.connectConnector(key, payload);
    try {
        const res = await axios.post(
            `${AEO_BASE}/businesses/${businessId}/connectors/${key}/connect`,
            payload,
            // The credential connectors are validated by a live call OUT to the
            // customer's own site, so this is the one AEO request that can hang
            // on a third party. Without a deadline the modal span "Checking…"
            // forever and the failure arrived as an unexplained network error;
            // with one it comes back as a timeout we can actually name.
            { headers: authHeaders(), timeout: CONNECT_TIMEOUT_MS },
        );
        const body = res.data?.data ?? res.data;
        if (body?.redirect_url) return { ok: true, kind: "redirect", url: String(body.redirect_url) };
        return { ok: true, kind: "connected", detail: String(body?.detail ?? "Connected") };
    } catch (err) {
        // The BE puts the human-readable reason in `message` and a stable code
        // in `errors[0]`, but its auth and permission guards answer in
        // FastAPI's `{ detail }` shape instead — and a blocked or timed-out
        // request carries no body at all. readApiError covers all three, so a
        // failed connect names its own cause instead of "please try again".
        const { message, code } = readApiError(err, "Could not connect.");
        catchErrorLog(`[aeo] connectConnector ${key} failed for ${businessId}: ${err}`);
        return { ok: false, message, code, sites: readWixCandidates(err) };
    }
}

/** What one on-demand connector pull came back with. */
export interface AeoConnectorSyncState {
    key: string;
    status: "connected" | "action_needed" | "not_connected";
    detail: string;
    lastSync: string | null;
}

export type ConnectorSyncResult =
    /** The pull ran. `status: "action_needed"` means it ran and FAILED — that
     *  is an answer for the connector card to display, not an error. */
    | { ok: true; connector: AeoConnectorSyncState }
    /** We could not even ask: never connected (404 `not_connected`), an
     *  unresolvable business id (400), or a network failure. */
    | { ok: false; message: string; code?: string };

/**
 * POST /api/v1/businesses/{id}/connectors/{key}/sync — re-pull ONE connector on
 * demand, without the paid full re-scan that "Rerun assessment" triggers.
 *
 * A failed pull is NOT an error response: the BE answers 200 with
 * `status: "action_needed"` and a human-readable `detail`. Callers render that
 * detail where the connector's status already lives and must not toast it as a
 * crash. After a successful sync the caller must re-fetch BOTH the connector
 * catalog and the business detail — the search numbers live on the business
 * detail payload, not on the connector.
 */
export async function syncConnector(
    businessId: string,
    key: string,
): Promise<ConnectorSyncResult> {
    if (useFixtures) {
        return { ok: false, message: "Refreshing is disabled while Oura is serving fixture data." };
    }
    try {
        const res = await axios.post(
            `${AEO_BASE}/businesses/${businessId}/connectors/${key}/sync`,
            {},
            { headers: authHeaders() },
        );
        const body = res.data?.data ?? res.data;
        // An out-of-set status falls to "action_needed" rather than "connected":
        // a surprise value should ask for attention, never invent a green tick.
        const status =
            body?.status === "connected" || body?.status === "not_connected"
                ? body.status
                : "action_needed";
        return {
            ok: true,
            connector: {
                key: String(body?.key ?? key),
                status,
                detail: String(body?.detail ?? ""),
                lastSync: body?.lastSync ?? null,
            },
        };
    } catch (err) {
        const read = readApiError(err, "Could not refresh.");
        const code = read.code;
        const message =
            code === "not_connected"
                ? "This connector was never connected, so there is nothing to refresh."
                : read.message;
        catchErrorLog(`[aeo] syncConnector ${key} failed for ${businessId}: ${err}`);
        return { ok: false, message, code };
    }
}

export type PageSpeedSyncResult =
    /** `failedStrategies` is the BE's own partial-failure signal: a 200 can mean
     *  "mobile refreshed, desktop did not" — the failed strategy silently keeps
     *  its LAST GOOD run while `capturedAt` moves to now. This used to be
     *  discarded, so a half-failed refresh reported "PageSpeed re-run complete"
     *  and the card then presented a week-old desktop run under a fresh
     *  timestamp. Empty array = everything asked for came back. */
    | { ok: true; failedStrategies: string[] }
    | { ok: false; message: string };

/**
 * POST /api/v1/businesses/{id}/pagespeed/sync (ourteam-backend#4352) — re-run
 * PageSpeed Insights for one business on demand, without the paid full re-scan
 * that "Rerun assessment" triggers. Same shape as the per-connector /sync.
 *
 * The refreshed block is persisted server-side against the business, so the
 * caller re-fetches the business detail (refreshLiveData) rather than patching
 * state from this response. On failure the previously captured numbers must
 * stay on screen — the caller renders the message, never blanks the card.
 */
export async function syncPageSpeed(businessId: string): Promise<PageSpeedSyncResult> {
    if (useFixtures) {
        return { ok: false, message: "Re-running PageSpeed is disabled while Oura is serving fixture data." };
    }
    try {
        const res = await axios.post(
            `${AEO_BASE}/businesses/${businessId}/pagespeed/sync`,
            { strategies: ["mobile", "desktop"] },
            { headers: authHeaders() },
        );
        // `data.failedStrategies` is set by the endpoint on every 200 (see
        // sync_pagespeed in aeo_business.py). Read defensively: an older BE
        // that predates it sends nothing, which is correctly "none failed".
        const failed = (res?.data?.data as { failedStrategies?: unknown } | undefined)?.failedStrategies;
        return {
            ok: true,
            failedStrategies: Array.isArray(failed) ? failed.filter((s): s is string => typeof s === "string") : [],
        };
    } catch (err) {
        const res = (err as { response?: { status?: number; data?: { message?: string } } }).response;
        // The endpoint ships behind ourteam-backend#4352 — a 404 means the BE
        // simply doesn't have it yet, which deserves better than "try again".
        const message =
            res?.status === 404
                ? "On-demand PageSpeed re-runs aren't available on this server yet."
                : res?.data?.message || "Could not re-run PageSpeed. Please try again.";
        catchErrorLog(`[aeo] syncPageSpeed failed for ${businessId}: ${err}`);
        return { ok: false, message };
    }
}

/** DELETE /api/v1/businesses/{id}/connectors/{key} — revoke a connection. */
export async function disconnectConnector(businessId: string, key: string): Promise<boolean> {
    if (useFixtures) return false;
    try {
        await axios.delete(`${AEO_BASE}/businesses/${businessId}/connectors/${key}`, {
            headers: authHeaders(),
        });
        return true;
    } catch (err) {
        catchErrorLog(`[aeo] disconnectConnector ${key} failed for ${businessId}: ${err}`);
        return false;
    }
}

// ─── Remediation: drafts and publications (ourteam-backend#3804) ─────────────
// Actions and Tracker tasks are ONE concept (#3801 B2) — there is no actions
// table. Everything here hangs off a tracker task via `trackerTaskId`.

/**
 * POST /api/v1/businesses/{id}/drafts — generate a fix draft for one page +
 * issue. Nothing is published: a draft is content a human reviews.
 *
 * `null` on failure rather than a throw, so a drawer can render an error state
 * without the caller unpacking an axios error.
 */
export async function createDraft(
    businessId: string,
    input: AeoDraftCreateRequest,
): Promise<AeoDraft | null> {
    if (useFixtures) return devMock.createDraft(businessId, input);
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${businessId}/drafts`, input, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoDraft;
    } catch (err) {
        catchErrorLog(`[aeo] createDraft failed for ${businessId}: ${err}`);
        return null;
    }
}

/** GET /api/v1/businesses/{id}/drafts — every draft for a business, optionally
 *  narrowed to the tracker task that owns them. */
export async function listDrafts(businessId: string, trackerTaskId?: string): Promise<AeoDraft[]> {
    if (useFixtures) return devMock.listDrafts(businessId, trackerTaskId);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/drafts`, {
            headers: authHeaders(),
            params: trackerTaskId ? { tracker_task_id: trackerTaskId } : undefined,
        });
        const body = res.data?.data ?? res.data;
        return Array.isArray(body) ? body : [];
    } catch (err) {
        catchErrorLog(`[aeo] listDrafts failed for ${businessId}: ${err}`);
        return [];
    }
}

/** GET /api/v1/drafts/{id}/preview — the before/after for a draft, so a
 *  reviewer sees what would change before approving a write to a live site. */
export async function previewDraft(draftId: string): Promise<Record<string, unknown> | null> {
    if (useFixtures) return devMock.previewDraft(draftId);
    try {
        const res = await axios.get(`${AEO_BASE}/drafts/${draftId}/preview`, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as Record<string, unknown>;
    } catch (err) {
        catchErrorLog(`[aeo] previewDraft failed for ${draftId}: ${err}`);
        return null;
    }
}

/**
 * POST /api/v1/drafts/{id}/publish — write the draft to the client's LIVE site.
 *
 * `approve` is sent explicitly and defaults to false on the BE for a reason:
 * this is the one call in the product that mutates someone else's production
 * website. The UI must never send it as a side effect of opening a preview.
 *
 * Returns the resulting publication, or an error message to surface verbatim —
 * a failed write ("WordPress rejected the credentials") is information the user
 * needs, not something to flatten into a generic failure.
 */
export async function publishDraft(
    draftId: string,
): Promise<{ ok: true; publication: AeoPublication } | { ok: false; message: string; refusal?: PublishRefusal | null }> {
    if (useFixtures) return devMock.publishDraft(draftId);
    try {
        const res = await axios.post(
            `${AEO_BASE}/drafts/${draftId}/publish`,
            { approve: true },
            { headers: authHeaders() },
        );
        return { ok: true, publication: (res.data?.data ?? res.data) as AeoPublication };
    } catch (err) {
        catchErrorLog(`[aeo] publishDraft failed for ${draftId}: ${err}`);
        const message = axios.isAxiosError(err)
            ? (err.response?.data?.message as string) || err.message
            : "Publish failed";
        // H20's gate: a refusal names the doc that still needs the client's yes.
        return { ok: false, message, refusal: readPublishRefusal(err) };
    }
}

/** GET /api/v1/businesses/{id}/publications — the write history for a business. */
export async function listPublications(businessId: string): Promise<AeoPublication[]> {
    if (useFixtures) return devMock.listPublications();
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/publications`, {
            headers: authHeaders(),
        });
        const body = res.data?.data ?? res.data;
        return Array.isArray(body) ? body : [];
    } catch (err) {
        catchErrorLog(`[aeo] listPublications failed for ${businessId}: ${err}`);
        return [];
    }
}

/** POST /api/v1/publications/{id}/revert — restore the previous value. Only
 *  possible when the publication captured one (`reversible`). */
export async function revertPublication(publicationId: string): Promise<boolean> {
    if (useFixtures) return devMock.revertPublication(publicationId);
    try {
        await axios.post(`${AEO_BASE}/publications/${publicationId}/revert`, {}, {
            headers: authHeaders(),
        });
        return true;
    } catch (err) {
        catchErrorLog(`[aeo] revertPublication failed for ${publicationId}: ${err}`);
        return false;
    }
}

// ─── Execution tracker (ourteam-backend#3686) ────────────────────────────────

/**
 * GET /api/v1/assessments/{id}/tracker — the four-week board, scoreboard and
 * fix register for one assessment.
 *
 * Generated on first fetch and persisted server-side, so calling this is what
 * CREATES a business's tracker. It is safe to call repeatedly — subsequent
 * fetches return the stored plan with whatever edits have been made to it.
 *
 * `null` on failure rather than a throw: the caller renders the existing
 * "not available" state instead of a broken screen.
 */
export async function getTracker(assessmentId: string): Promise<AeoTracker | null> {
    if (useFixtures) return devMock.getTracker();
    try {
        const res = await axios.get(`${AEO_BASE}/assessments/${assessmentId}/tracker`, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoTracker;
    } catch (err) {
        catchErrorLog(`[aeo] getTracker failed for ${assessmentId}: ${err}`);
        return null;
    }
}

/**
 * GET /api/v1/assessments/{id}/tracker/summary — the plan's task list, read-only.
 *
 * Separate from `getTracker` above for one reason: that route GENERATES the
 * tracker it returns. Anything that only wants to ASK the plan a question has
 * to come here instead, or merely opening a drawer would mint a four-week plan
 * for a business nobody has been sold.
 *
 * `null` on failure. Note that this is NOT the same as `{exists: false}`: the
 * latter is a successful answer meaning no plan has been generated yet, and the
 * caller should stay silent rather than claim a fix is unplanned.
 */
export async function getTrackerSummary(assessmentId: string): Promise<AeoTrackerSummary | null> {
    // fixture-only: the summary of the one fixture plan `getTracker` serves.
    if (useFixtures) return devMock.getTrackerSummary();
    try {
        const res = await axios.get(`${AEO_BASE}/assessments/${assessmentId}/tracker/summary`, {
            headers: authHeaders(),
        });
        const data = (res.data?.data ?? res.data) as AeoTrackerSummary;
        return { exists: !!data?.exists, tasks: data?.tasks ?? [] };
    } catch (err) {
        catchErrorLog(`[aeo] getTrackerSummary failed for ${assessmentId}: ${err}`);
        return null;
    }
}

const AUDIT_STATUSES: readonly AeoScanAuditStatus[] = ["clean", "issues", "error", "not_audited"];

/**
 * GET /api/v1/assessments/{id}/audit (ourteam-backend#5910) — the automatic
 * audit of a finished scan. Staff and service only: a client login is refused,
 * and the caller must not ask for it (the badge is never mounted for a client).
 *
 * `null` for anything that is not a readable audit: a failed call, a refusal, a
 * sample business, or a status this build does not know. The badge then shows
 * nothing, because "no answer" is not "clean". Never throws.
 */
export async function getScanAudit(assessmentId: string): Promise<AeoScanAudit | null> {
    // No sample audit: a fixture business has no server-side audit to read.
    if (useFixtures) return null;
    try {
        const res = await axios.get(`${AEO_BASE}/assessments/${assessmentId}/audit`, {
            headers: authHeaders(),
        });
        const data = (res.data?.data ?? res.data) as Partial<AeoScanAudit> | undefined;
        if (!data || !AUDIT_STATUSES.includes(data.status as AeoScanAuditStatus)) return null;
        return {
            assessment_id: typeof data.assessment_id === "string" ? data.assessment_id : assessmentId,
            status: data.status as AeoScanAuditStatus,
            checkedAt: typeof data.checkedAt === "string" ? data.checkedAt : null,
            trigger: typeof data.trigger === "string" ? data.trigger : null,
            issues: Array.isArray(data.issues) ? data.issues : [],
            notes: Array.isArray(data.notes) ? data.notes : [],
            fixed: Array.isArray(data.fixed) ? data.fixed : [],
            seconds: typeof data.seconds === "number" ? data.seconds : null,
        };
    } catch (err) {
        catchErrorLog(`[aeo] getScanAudit failed for ${assessmentId}: ${err}`);
        return null;
    }
}

/**
 * PATCH /api/v1/assessments/{id}/tracker — plan settings.
 *
 * WHEN the work happens: the start date every other date counts forward from,
 * the holidays that cannot be derived, and how long each block runs (#4477,
 * #4517). Not the same operation as rebuild below, which changes WHICH TASKS
 * exist.
 *
 * Returns the re-dated tracker. `null` on failure, so the caller can say the
 * save did not land rather than leave dates nobody chose on screen.
 */
export async function patchTrackerSettings(
    assessmentId: string,
    patch: AeoTrackerPatchBody,
): Promise<AeoTracker | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.patch(`${AEO_BASE}/assessments/${assessmentId}/tracker`, patch, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoTracker;
    } catch (err) {
        catchErrorLog(`[aeo] patchTrackerSettings failed for ${assessmentId}: ${err}`);
        return null;
    }
}

/**
 * POST /api/v1/assessments/{id}/tracker/rebuild — re-plan from the stored scan.
 *
 * No crawl and no engine probes: this exists because a tracker is generated once
 * and persisted, so a plan built before the template changed had no way to be
 * given the new one except a full re-assessment, which costs real money to fix
 * something that was never the client's site.
 *
 * Staff only, enforced server-side. Returns the new tracker AND a summary of
 * what changed, because swapping a plan silently is how someone loses track of
 * work they had already started.
 */
export async function rebuildTracker(assessmentId: string): Promise<AeoTrackerRebuild | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.post(
            `${AEO_BASE}/assessments/${assessmentId}/tracker/rebuild`,
            {},
            { headers: authHeaders() },
        );
        return (res.data?.data ?? res.data) as AeoTrackerRebuild;
    } catch (err) {
        catchErrorLog(`[aeo] rebuildTracker failed for ${assessmentId}: ${err}`);
        return null;
    }
}

/**
 * GET /api/v1/assessments/{id}/activity — the plan's delivery record.
 *
 * Scoped to the BUSINESS, not this assessment: entries are history, and a
 * re-assessment mints new task ids, so returning only this run's would leave
 * everything recorded under the previous plan unreachable.
 *
 * `null` on failure, `[]` on "nothing recorded here yet". Those are NOT the same
 * answer and this function used to spell them the same way, which meant an
 * expired session or a 500 rendered as an empty delivery record — an audit
 * trail reporting that no work was done. Of every call in this file, this is
 * the one where a silent failure does the most damage.
 *
 * Still no throw, matching the rest of the module: the caller decides what an
 * absent answer looks like on screen.
 */
export async function getPlanActivity(
    assessmentId: string,
    opts?: {
        from?: string;
        to?: string;
        week?: number;
        taskId?: string;
        /**
         * Which outcomes to return (ourteam-backend#4531).
         *
         * "What closed this week" and "what is blocked" have to be separable BY
         * QUERY rather than by reading prose, so the BE takes a comma-separated
         * list and one request can ask for both.
         */
        outcome?: AeoActivityEntry["outcome"][];
    },
): Promise<AeoActivityEntry[] | null> {
    if (useFixtures) return [];
    try {
        const res = await axios.get(`${AEO_BASE}/assessments/${assessmentId}/activity`, {
            headers: authHeaders(),
            params: {
                from: opts?.from,
                to: opts?.to,
                week: opts?.week,
                taskId: opts?.taskId,
                outcome: opts?.outcome?.length ? opts.outcome.join(",") : undefined,
            },
        });
        const data = (res.data?.data ?? res.data) as { entries?: AeoActivityEntry[] };
        return data?.entries ?? [];
    } catch (err) {
        catchErrorLog(`[aeo] getPlanActivity failed for ${assessmentId}: ${err}`);
        return null;
    }
}

/**
 * POST /api/v1/tracker/tasks/{taskId}/activity — append one entry.
 *
 * Append-only by design: there is no update path, and a correction is a new
 * entry. `at` and the author's user id are set by the server, so nothing here
 * sends a timestamp.
 */
export async function appendTaskActivity(
    taskId: string,
    entry: AeoActivityCreate,
): Promise<AeoActivityEntry | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.post(`${AEO_BASE}/tracker/tasks/${taskId}/activity`, entry, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoActivityEntry;
    } catch (err) {
        catchErrorLog(`[aeo] appendTaskActivity failed for ${taskId}: ${err}`);
        return null;
    }
}

/**
 * GET /api/v1/assessments/{id}/activity/summary — the material a weekly client
 * update is assembled from (ourteam-backend#4531 §3).
 *
 * Deliberately not a rendering of the log: it groups a date range into the
 * three questions the update asks (what closed, what is stuck, what only the
 * client can decide) and carries the entries underneath as evidence. Turning
 * them into sentences is the writer's job, not the API's.
 *
 * `null` on failure, matching `getPlanActivity` and for the same reason — an
 * empty summary and a failed one must not be spelled the same way when the
 * thing being reported is what work got done.
 */
export async function getActivitySummary(
    assessmentId: string,
    opts?: { from?: string; to?: string },
): Promise<AeoActivitySummary | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.get(
            `${AEO_BASE}/assessments/${assessmentId}/activity/summary`,
            { headers: authHeaders(), params: { from: opts?.from, to: opts?.to } },
        );
        return (res.data?.data ?? res.data) as AeoActivitySummary;
    } catch (err) {
        catchErrorLog(`[aeo] getActivitySummary failed for ${assessmentId}: ${err}`);
        return null;
    }
}

/**
 * POST /api/v1/assessments/{id}/suggestions/{sid}/keep|dismiss — decide one
 * proposal from the queue (ourteam-backend#4513).
 *
 * Both hand back the REGENERATED plan, so the caller replaces its tracker
 * rather than moving the suggestion itself. A kept suggestion becomes a real
 * task through the generator, which is what makes a later rebuild reproduce it
 * instead of dropping it; a caller that patched its own copy would be stating a
 * second version of the same decision.
 *
 * Staff-only on the BE: a client accepting their own proposals is not the
 * review step the queue exists to be.
 */
export async function decideSuggestion(
    assessmentId: string,
    suggestionId: string,
    decision: "keep" | "dismiss",
): Promise<AeoSuggestionDecision | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.post(
            `${AEO_BASE}/assessments/${assessmentId}/suggestions/${suggestionId}/${decision}`,
            {},
            { headers: authHeaders() },
        );
        return (res.data?.data ?? res.data) as AeoSuggestionDecision;
    } catch (err) {
        catchErrorLog(`[aeo] decideSuggestion ${decision} failed for ${suggestionId}: ${err}`);
        return null;
    }
}

/**
 * PATCH /api/v1/businesses/{id}/checks/{checkKey}/pages — record what the
 * operator asserts about these pages and this check (ourteam-backend#4530 §3).
 *
 * A fix made by hand in WordPress, outside Oura, had no way of being recorded,
 * so the finding stayed open on every surface until the next crawl.
 *
 * Bulk in one request, not a loop of single writes: the ask was to change the
 * status of many pages at once, and fanning out per row from the browser is the
 * same feature with the complaint left in.
 *
 * This is a CLAIM. It never overwrites the crawl — the two are compared at read
 * time and a disagreement comes back on the page as `statusDisagreement`.
 */
export async function setPageCheckStatus(
    businessId: string,
    checkKey: string,
    urls: string[],
    status: "not_started" | "in_progress" | "done",
): Promise<boolean> {
    if (useFixtures) return true;
    if (urls.length === 0) return true;
    try {
        await axios.patch(
            `${AEO_BASE}/businesses/${businessId}/checks/${checkKey}/pages`,
            { urls, status },
            { headers: authHeaders() },
        );
        return true;
    } catch (err) {
        catchErrorLog(`[aeo] setPageCheckStatus failed for ${checkKey}: ${err}`);
        return false;
    }
}

/**
 * POST /api/v1/businesses/{id}/pages/status — take a placeholder page off the
 * live site (ourteam-backend#4529 §3).
 *
 * Two guards, both on the BE, both worth naming here because the UI has to
 * explain them rather than translate a 400 into "something went wrong":
 *
 *   - `approve: true` is REQUIRED. This is a destructive write to a client's
 *     live site, and leaving the confirmation entirely to the frontend means a
 *     mis-wired button can take a page down without anyone having said yes.
 *   - The page must already resolve to `placeholder` — a reviewer's label
 *     first, the crawl's inference second. A page the crawl never reached is
 *     "unknown", never a placeholder: we cannot take something down on the
 *     strength of never having read it. That refusal is a 409 whose message
 *     names WHICH of those applied, so it is surfaced verbatim.
 *
 * `draft` is the reversible one and is what the UI should offer; `trash` is
 * WordPress's bin.
 */
export async function setPageStatus(
    businessId: string,
    url: string,
    action: "draft" | "trash",
): Promise<{ ok: true } | { ok: false; kind: "not_a_placeholder" | "error"; message: string }> {
    if (useFixtures) return { ok: true };
    try {
        await axios.post(
            `${AEO_BASE}/businesses/${businessId}/pages/status`,
            { url, action, approve: true },
            { headers: authHeaders() },
        );
        return { ok: true };
    } catch (err) {
        catchErrorLog(`[aeo] setPageStatus ${action} failed for ${url}: ${err}`);
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const message = axios.isAxiosError(err)
            ? (err.response?.data?.message as string) || err.message
            : "Could not change the page";
        return { ok: false, kind: status === 409 ? "not_a_placeholder" : "error", message };
    }
}

/**
 * PATCH /api/v1/tracker/tasks/{taskId} — set a status, move a task between
 * weeks, or drop it from the plan. Send only the field that changed.
 *
 * Returns the WHOLE tracker back, because a status change can move the current
 * week and re-derive the next deliverable — so the caller replaces its copy
 * rather than patching one task in place and drifting from the server.
 *
 * `null` on failure. The caller has already applied the change optimistically,
 * so a null here means "the board on screen is ahead of the server", which is
 * worth surfacing rather than swallowing.
 */
export async function patchTrackerTask(
    taskId: string,
    patch: AeoTrackerTaskPatch,
): Promise<AeoTracker | null> {
    if (useFixtures) return devMock.patchTrackerTask(taskId, patch);
    try {
        const res = await axios.patch(`${AEO_BASE}/tracker/tasks/${taskId}`, patch, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoTracker;
    } catch (err) {
        catchErrorLog(`[aeo] patchTrackerTask failed for ${taskId}: ${err}`);
        return null;
    }
}

// ─── Pages Workshop (epic ourteam-backend#4104) ──────────────────────────────
//
// Read path only. The publish call (#4100) is deliberately NOT here: whether a
// block-scoped copy edit may be written to a client's live site is an open
// product question, and the answer changes whether the button says Approve or
// Export. Wiring a write we might have to relabel or remove would be building
// on a decision nobody has made.
//
// Every function returns null on failure rather than throwing, so the workshop
// renders an error state instead of a blank screen.

/**
 * GET /api/v1/businesses/{id}/pages?url= — the page as ordered tagged blocks.
 *
 * Read LIVE, not from the crawl snapshot: a proposal is anchored to a verbatim
 * string, so the document the reviewer reads must be the one that is live now.
 * The BE pins the fetch to the business's own domain, so an off-domain url is
 * refused by design.
 */
export async function getPageContent(
    businessId: string,
    url: string,
): Promise<AeoPageContent | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/pages`, {
            params: { url },
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoPageContent;
    } catch (err) {
        catchErrorLog(`[aeo] getPageContent failed for ${url}: ${err}`);
        return null;
    }
}

/** GET /api/v1/businesses/{id}/link-graph — the edges the crawl already
 *  traversed. A 404 says WHICH absence it is; empty edges would read as "this
 *  site has no internal links", which is a different claim. */
/**
 * Every role correction stored for this business, in one request (#4368 §2).
 *
 * The workshop reads one page at a time because that is what it is looking at.
 * The Pages table lists a whole crawl, 30 to 200 rows, and cannot ask per row —
 * so without this it could not read back what anybody had labelled, and one
 * reviewer's corrections stayed invisible to everyone else. That is the opposite
 * of the point: somebody sorts the placeholder pages out once and the team sees
 * it.
 *
 * `[]` on failure rather than null: a missing correction list degrades to the
 * inferred roles, which is what the table showed before this existed.
 */
export async function getPageRoles(businessId: string): Promise<AeoPageRoleOverride[]> {
    return (await getPageRolesResult(businessId)) ?? [];
}

/**
 * The same read, with a failure kept apart from "no overrides": null when the
 * request failed. The client's Website page needs the difference, because
 * counting issues without the set-aside pages would publish figures staff
 * excluded (Jack's review of #4210). Staff Site keeps `getPageRoles`.
 */
export async function getPageRolesResult(businessId: string): Promise<AeoPageRoleOverride[] | null> {
    if (useFixtures) return [];
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/pages/roles`, {
            headers: authHeaders(),
        });
        const body = res.data?.data ?? res.data;
        const roles = body?.roles;
        return Array.isArray(roles) ? (roles as AeoPageRoleOverride[]) : [];
    } catch (err) {
        catchErrorLog(`[aeo] getPageRoles failed for ${businessId}: ${err}`);
        return null;
    }
}

export async function getLinkGraph(businessId: string): Promise<AeoLinkGraph | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/link-graph`, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoLinkGraph;
    } catch (err) {
        catchErrorLog(`[aeo] getLinkGraph failed for ${businessId}: ${err}`);
        return null;
    }
}

/**
 * Pull a draft out of whatever envelope it arrived in.
 *
 * The handoff pinned the proposal SHAPE but not the response wrapper, and the
 * two endpoints that return one do not have to agree. Guessing wrong is not a
 * visible failure — `recommendations` reads as `undefined`, becomes `[]`, and
 * the workshop truthfully reports "no proposals for this page" about a draft
 * that exists. That is exactly the shape of the Kheng Dental report, where
 * History listed 20 proposals and the proposals pane showed none.
 *
 * So accept the plausible envelopes rather than one. Returning null (not an
 * empty draft) when nothing matches keeps "we could not find a draft" distinct
 * from "there is no draft".
 */
function normalizeDraft(body: unknown): AeoPageDraft | null {
    const seen = new Set<unknown>();
    const isEmpty = (d: AeoPageDraft) => (d.recommendations ?? []).length === 0;

    /**
     * Prefer a draft that carries proposals over one that does not.
     *
     * When a response holds several candidates — a list of drafts, or several
     * plausible envelope keys — taking the first match is a coin toss on the
     * BE's ordering. Getting it wrong reads as "no proposals for this page"
     * about a page that has them, which is the exact bug this whole PR exists
     * to stop. An empty draft is still returned when it is genuinely the only
     * one, because "a draft with zero proposals" is a real state and must stay
     * distinct from "no draft found" (`null`).
     */
    const pick = (candidates: unknown[], depth: number): AeoPageDraft | null => {
        let fallback: AeoPageDraft | null = null;
        for (const c of candidates) {
            const hit = dig(c, depth + 1);
            if (!hit) continue;
            if (!isEmpty(hit)) return hit;
            if (!fallback) fallback = hit;
        }
        return fallback;
    };

    const dig = (node: unknown, depth: number): AeoPageDraft | null => {
        if (!node || typeof node !== "object" || depth > 3 || seen.has(node)) return null;
        seen.add(node);
        // A list of drafts — newest first per redraft-supersedes, but a
        // superseded or not-yet-populated entry can lead.
        if (Array.isArray(node)) return pick(node, depth);
        const obj = node as Record<string, unknown>;
        if (Array.isArray(obj.recommendations)) return obj as unknown as AeoPageDraft;
        return pick(
            ["data", "draft", "drafts", "result"].map((k) => obj[k]),
            depth,
        );
    };
    return dig(body, 0);
}

/**
 * The same job for a CREATE response — and deliberately not the same rule.
 *
 * "Prefer a draft that has proposals" is a read-path heuristic: on a read, any
 * draft carrying proposals is a better answer than an empty one, because we are
 * asking "what is there?".
 *
 * A create is asking something else: "what did the redraft just produce?" There
 * the answer is a specific object, and an empty one is a real, correct answer —
 * a redraft can legitimately find nothing to change on a page that is already
 * good. If the response also carries older drafts, preferring the non-empty one
 * would show a SUPERSEDED draft's proposals as though the operator's redraft
 * had produced them. That is a worse failure than an empty pane: it is wrong,
 * and it looks right.
 *
 * So this walks only the direct envelopes and never searches lists. When none
 * of them matches it returns null and the caller reports the redraft as
 * unreadable, rather than answering the question with the wrong draft. The
 * draft still exists server-side; the next read picks it up.
 */
function normalizeCreatedDraft(body: unknown): AeoPageDraft | null {
    const top = body as Record<string, unknown> | null | undefined;
    const data = top?.data as Record<string, unknown> | undefined;
    const direct = [top, data, data?.draft, top?.draft, top?.result];
    for (const candidate of direct) {
        if (candidate && typeof candidate === "object") {
            const obj = candidate as Record<string, unknown>;
            if (Array.isArray(obj.recommendations)) return obj as unknown as AeoPageDraft;
        }
    }
    return null;
}

/** GET /api/v1/businesses/{id}/pages/draft?url= — the current draft, re-anchored
 *  against the live page on every read. `match`/`matched_text` can therefore
 *  change between two reads of the same draft; render what the response says
 *  and never cache the anchors. */
export async function getPageDraft(
    businessId: string,
    url: string,
    includeSuperseded = false,
): Promise<AeoPageDraft | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/pages/draft`, {
            params: includeSuperseded ? { url, include_superseded: true } : { url },
            headers: authHeaders(),
        });
        return normalizeDraft(res.data);
    } catch (err) {
        catchErrorLog(`[aeo] getPageDraft failed for ${url}: ${err}`);
        return null;
    }
}

/**
 * POST /api/v1/businesses/{id}/pages/draft — generate or redraft.
 *
 * This is a real model call against a real client's page: it costs money and
 * takes time. Callers must drive it from an explicit user action and show a
 * pending state, never fire it on render.
 */
export async function createPageDraft(
    businessId: string,
    input: AeoPageDraftRequest,
): Promise<AeoPageDraft | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.post(
            `${AEO_BASE}/businesses/${businessId}/pages/draft`,
            input,
            { headers: authHeaders() },
        );
        return normalizeCreatedDraft(res.data);
    } catch (err) {
        catchErrorLog(`[aeo] createPageDraft failed for ${input.url}: ${err}`);
        return null;
    }
}

/**
 * POST /api/v1/businesses/{id}/pages/publish — write a page's approved changes
 * to the client's LIVE WordPress site (#4100).
 *
 * This is the one call in the workshop that mutates someone else's production
 * page, so `approve: true` is added here rather than left to a caller who might
 * forget it, and the BE refuses the write without it.
 *
 * INCLUSION IS THE DEFAULT. The reviewer reads each proposal and dismisses the
 * ones they disagree with; `dismissed` carries exactly those ids, and the BE
 * publishes everything else. Sending the kept ids instead would invert the
 * safe default — a forgotten proposal would silently go live rather than
 * silently stay a draft.
 *
 * The failures are separated because they lead to different actions. `drift`
 * (409) means the live page changed under the draft and an anchored span no
 * longer matches; the BE names which, and the fix is Redraft. `no_connector`
 * (409) means there is no connected WordPress site. Both messages are the BE's
 * own words — they carry the drifted spans or the missing connector, and that
 * detail is the whole point.
 */
export async function publishPageChanges(
    businessId: string,
    input: { url: string; draftId: string; dismissed?: string[] },
): Promise<AeoPagePublishResult> {
    if (useFixtures) {
        return { ok: false, kind: "error", message: "Publishing is not available on a sample business." };
    }
    try {
        const res = await axios.post(
            `${AEO_BASE}/businesses/${businessId}/pages/publish`,
            {
                url: input.url,
                draft_id: input.draftId,
                dismissed: input.dismissed ?? [],
                approve: true,
            },
            { headers: authHeaders() },
        );
        const data = (res.data?.data ?? res.data) as {
            publication_id: string;
            url: string;
            via: string;
            published_at: string | null;
            changes?: AeoPageHistoryChange[];
            reversible?: boolean;
        };
        return {
            ok: true,
            publicationId: data.publication_id,
            url: data.url,
            via: data.via,
            publishedAt: data.published_at ?? null,
            changes: data.changes ?? [],
            reversible: data.reversible !== false,
        };
    } catch (err) {
        catchErrorLog(`[aeo] publishPageChanges failed for ${input.url}: ${err}`);
        const code = axios.isAxiosError(err) ? err.response?.data?.errors?.[0] : undefined;
        const message = axios.isAxiosError(err)
            ? (err.response?.data?.message as string) || err.message
            : "Could not publish the changes";
        // The BE returns a stable `errors[0]` code alongside the human message.
        // Mapped to the union so the screen can react to the KIND (redraft vs
        // connect a site) while still showing the message it was given.
        const kind: Extract<AeoPagePublishResult, { ok: false }>["kind"] =
            code === "anchor_drifted"
                ? "drift"
                : code === "no_publish_connector"
                    ? "no_connector"
                    : code === "nothing_approved"
                        ? "nothing_approved"
                        : code === "copy_publish_not_permitted"
                            ? "copy_blocked"
                            : "error";
        return { ok: false, kind, message, refusal: readPublishRefusal(err) };
    }
}

/** GET /api/v1/businesses/{id}/pages/history?url= — drafts, redrafts and
 *  publications for one page, each naming the actual changes. */
export async function getPageHistory(
    businessId: string,
    url: string,
): Promise<AeoPageHistoryEntry[]> {
    if (useFixtures) return [];
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/pages/history`, {
            params: { url },
            headers: authHeaders(),
        });
        const body = res.data?.data ?? res.data;
        // Shape not pinned in the handoff — accept a bare array or an
        // `events`-wrapped one rather than rendering nothing on a guess.
        const events = Array.isArray(body) ? body : (body?.events ?? []);
        return events as AeoPageHistoryEntry[];
    } catch (err) {
        catchErrorLog(`[aeo] getPageHistory failed for ${url}: ${err}`);
        return [];
    }
}

/**
 * PATCH /api/v1/businesses/{id}/pages — correct a page's role.
 *
 * Stored in its own table server-side and read ahead of every inferred signal,
 * so a re-scan can never overwrite it. That is the whole reason it is not part
 * of the scan payload: a correction a crawl can undo is not a correction.
 */
export async function setPageRole(
    businessId: string,
    url: string,
    role: AeoPageRole,
): Promise<boolean> {
    if (useFixtures) return true;
    try {
        await axios.patch(
            `${AEO_BASE}/businesses/${businessId}/pages`,
            { url, role },
            { headers: authHeaders() },
        );
        return true;
    } catch (err) {
        catchErrorLog(`[aeo] setPageRole failed for ${url}: ${err}`);
        return false;
    }
}

/**
 * A signed URL for one question's ChatGPT screenshot (#3564).
 *
 * Two hops on purpose. The image endpoint needs the user's Authorization
 * header, which an `<img src>` cannot send, so this asks for a signed URL and
 * the caller sets THAT as the src.
 *
 * The URL expires in about 15 minutes, so a page left open long enough has to
 * ask again — `expires_at` is returned so a caller can tell rather than
 * discover it from a broken image.
 *
 * `null` means there is no screenshot for that question: an older scan, or an
 * upload that failed. The answer text is still worth showing, so this is not an
 * error.
 */
export async function getAnswerScreenshotUrl(
    id: string,
    n: number,
): Promise<{ url: string; expiresAt: number } | null> {
    if (useFixtures) return null;
    try {
        const res = await axios.get(
            `${AEO_BASE}/assessments/${id}/app-measurement/screenshot/${n}`,
            { headers: authHeaders() },
        );
        const data = (res.data?.data ?? res.data) as
            | { url?: string; expiresInSeconds?: number }
            | undefined;
        if (!data?.url) return null;
        // Renew a minute early rather than on the stroke: a request that starts
        // at 14:59 can still arrive after the URL has died.
        const lifetime = Math.max(60, (data.expiresInSeconds ?? 900) - 60);
        return { url: data.url, expiresAt: Date.now() + lifetime * 1000 };
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) return null;
        catchErrorLog(`[aeo] getAnswerScreenshotUrl failed for ${id}#${n}: ${err}`);
        return null;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Knowledge sources (ourteam-backend#4749)
//
//   GET    /businesses/{id}/knowledge          list, newest first
//   POST   /businesses/{id}/knowledge          multipart, 1..N files
//   POST   /businesses/{id}/knowledge/text     { title, text, label?, content_as_of? }
//   PATCH  /businesses/{id}/knowledge/{sid}    { title?, label?, content_as_of?, status? }
//   DELETE /businesses/{id}/knowledge/{sid}    soft-delete
//   GET    /businesses/{id}/knowledge/{sid}/content   text + file link (asked for, not built yet)
//   POST   /knowledge/staged?domain=<domain>   pre-scan staging, same bodies
//   GET    /knowledge/staged?domain=<domain>   pre-scan list, { sources, limits } (#4994)
//   DELETE /knowledge/staged/{sid}?domain=     pre-scan soft-delete (#4994)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The server-side caps, mirrored so the UI can show how close a business is to
 * them before a write is refused.
 *
 * Mirrored, NOT owned: ourteam-backend#4749 enforces all three, and if it ever
 * changes one, this drifts. Worth having anyway — a character budget nobody can
 * see is one people only learn about by hitting it. Asked for on the ticket as
 * fields on the list response; until then a stale number here is a cosmetic
 * error and the server still refuses correctly.
 */
export const KNOWLEDGE_CHAR_BUDGET = 2_000_000;
export const KNOWLEDGE_MAX_SOURCES = 25;
export const KNOWLEDGE_MAX_FILE_BYTES = 20 * 1024 * 1024;

/** Extraction runs inline under a 20s cap server-side; allow for the upload. */
const KNOWLEDGE_UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Auth WITHOUT a Content-Type, for the multipart writes.
 *
 * `authHeaders()` hardcodes `application/json`, which is right for every JSON
 * body on this module and fatal for a FormData one: axios sends exactly the
 * Content-Type it is given, so the browser never gets to write the
 * `multipart/form-data; boundary=…` that makes the body parseable. The server
 * then sees no parts at all and answers 422 `body.files: Field required` with
 * `input: null` — a message that reads like a missing field and is really a
 * missing boundary.
 *
 * This is why `uploadFile` in clientReview.ts sends only Authorization, and why
 * this exists as its own named function rather than a header object spelled out
 * at each call site: the next person to add an upload here should reach for a
 * thing whose name says what it is for.
 */
function multipartAuthHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function knowledgeError(err: unknown, title: string): KnowledgeAddResult {
    const status = (err as { response?: { status?: number } }).response?.status;
    const { message } = readApiError(err, "That could not be added.");
    // 409 is the dedupe rule, and it is not a failure the operator caused or
    // has to fix — the content is already here. It gets its own row and its own
    // words, so nobody goes looking for what they did wrong.
    if (status === 409) return { ok: false, kind: "duplicate", title, message };
    catchErrorLog(`[aeo] knowledge write failed for ${title}: ${err}`);
    return { ok: false, kind: "error", title, message };
}

/**
 * The rows out of a write response.
 *
 * Three shapes reach here, and all three are real: the file and staged routes
 * answer `{ sources: [...] }`, the text route answers a bare row object, and
 * the mock hands back an array. Reading only one of them is how the first cut
 * of this turned a 201 carrying four files into a single unrenderable row.
 */
function asSources(body: unknown): AeoKnowledgeSource[] {
    if (Array.isArray(body)) return body as AeoKnowledgeSource[];
    const wrapped = (body as { sources?: unknown } | null)?.sources;
    if (Array.isArray(wrapped)) return wrapped as AeoKnowledgeSource[];
    return body ? [body as AeoKnowledgeSource] : [];
}

/**
 * GET — every live source for the business, newest first, plus the caps.
 *
 * The payload is `{ sources, limits }`, NOT a bare array. Reading it as an array
 * is silent: the tab renders its empty state on a business that has ten sources
 * and nothing anywhere says why.
 */
export async function listKnowledge(businessId: string): Promise<KnowledgePage> {
    if (useFixtures) return devMock.listKnowledge(businessId);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/knowledge`, { headers: authHeaders() });
        const body = (res.data?.data ?? res.data) as { sources?: unknown; limits?: KnowledgeLimits } | null;
        const sources = Array.isArray(body) ? body : Array.isArray(body?.sources) ? body!.sources : [];
        return { sources: sources as AeoKnowledgeSource[], limits: body?.limits };
    } catch (err) {
        catchErrorLog(`[aeo] listKnowledge failed for ${businessId}: ${err}`);
        return { sources: [] };
    }
}

/**
 * Upload ONE file.
 *
 * The endpoint takes 1..N per request, and this deliberately sends one. The
 * dedupe rule answers 409 for the whole request, so a batch containing one file
 * already on the business has no way to tell the caller which one — and the
 * screen's job here is exactly that: one row per file, each with its own
 * outcome, so three good files still land when the fourth is a repeat.
 *
 * The cost is N requests for N files. That is the right trade for a control
 * where the failure is "your upload silently did nothing".
 */
export async function uploadKnowledgeFile(
    businessId: string,
    file: File,
    opts?: { label?: string; contentAsOf?: string },
): Promise<KnowledgeAddResult> {
    if (useFixtures) return devMock.uploadKnowledgeFile(businessId, file, opts);
    const form = new FormData();
    form.append("files", file);
    if (opts?.label) form.append("label", opts.label);
    if (opts?.contentAsOf) form.append("content_as_of", opts.contentAsOf);
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${businessId}/knowledge`, form, {
            headers: multipartAuthHeaders(),
            timeout: KNOWLEDGE_UPLOAD_TIMEOUT_MS,
        });
        return { ok: true, sources: asSources(res.data?.data ?? res.data) };
    } catch (err) {
        return knowledgeError(err, file.name);
    }
}

/** POST /text — a pasted note. Same row, same list, same budget as a file. */
export async function addKnowledgeText(
    businessId: string,
    input: { title: string; text: string; label?: string; content_as_of?: string },
): Promise<KnowledgeAddResult> {
    if (useFixtures) return devMock.addKnowledgeText(businessId, input);
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${businessId}/knowledge/text`, input, {
            headers: authHeaders(),
        });
        return { ok: true, sources: asSources(res.data?.data ?? res.data) };
    } catch (err) {
        return knowledgeError(err, input.title);
    }
}

/**
 * PATCH — retitle, relabel, re-date, or park a source.
 *
 * `status` only ever moves to or from `excluded`. Parking is not deleting: a
 * source that is misleading the drafts stops being read while the record that
 * the client sent it survives.
 */
export async function patchKnowledge(
    businessId: string,
    sourceId: string,
    patch: { title?: string; label?: string; content_as_of?: string; status?: "ready" | "excluded" },
): Promise<AeoKnowledgeSource | null> {
    if (useFixtures) return devMock.patchKnowledge(businessId, sourceId, patch);
    try {
        const res = await axios.patch(`${AEO_BASE}/businesses/${businessId}/knowledge/${sourceId}`, patch, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoKnowledgeSource;
    } catch (err) {
        catchErrorLog(`[aeo] patchKnowledge failed for ${sourceId}: ${err}`);
        return null;
    }
}

/**
 * GET /content — what is inside one source, so staff can read it.
 *
 * Asked of the backend (Knowledge reader handoff, 20 Oct 2026); until it ships
 * the route 404s and this answers null, which the reader shows as "can't show
 * the contents yet" rather than an empty page that looks like an empty file.
 * Any other failure rejects, so an outage is not passed off as that state.
 */
export async function getKnowledgeContent(businessId: string, sourceId: string): Promise<KnowledgeContent | null> {
    if (useFixtures) return devMock.getKnowledgeContent(businessId, sourceId);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/knowledge/${sourceId}/content`, {
            headers: authHeaders(),
        });
        const data = (res.data?.data ?? res.data) as { text?: string | null; fileUrl?: string | null } | null;
        if (!data) return null;
        return { text: data.text ?? null, fileUrl: data.fileUrl ?? null };
    } catch (err) {
        if (axios.isAxiosError(err) && (err.response?.status === 404 || err.response?.status === 405)) return null;
        // Anything else is a real failure, not the pending route: the reader
        // says it could not load rather than "can't show the contents yet".
        catchErrorLog(`[aeo] getKnowledgeContent failed for ${sourceId}: ${err}`);
        throw err;
    }
}

/** DELETE — soft-deletes the row and hard-deletes the object and its text. */
export async function deleteKnowledge(businessId: string, sourceId: string): Promise<boolean> {
    if (useFixtures) return devMock.deleteKnowledge(businessId, sourceId);
    try {
        await axios.delete(`${AEO_BASE}/businesses/${businessId}/knowledge/${sourceId}`, { headers: authHeaders() });
        return true;
    } catch (err) {
        catchErrorLog(`[aeo] deleteKnowledge failed for ${sourceId}: ${err}`);
        return false;
    }
}

/**
 * Staging, for New scan.
 *
 * A business row is created by the FIRST assessment, so Step 2 has no business
 * id to attach anything to. Staged rows are written against the normalised
 * domain and re-parented by `POST /assessments`. That ordering is the only
 * reason this route exists; everything else about a staged source is identical.
 */
export async function stageKnowledgeFile(
    domain: string,
    file: File,
    opts?: { label?: string; contentAsOf?: string },
): Promise<KnowledgeAddResult> {
    if (useFixtures) return devMock.stageKnowledgeFile(domain, file, opts);
    const form = new FormData();
    form.append("files", file);
    if (opts?.label) form.append("label", opts.label);
    if (opts?.contentAsOf) form.append("content_as_of", opts.contentAsOf);
    try {
        const res = await axios.post(`${AEO_BASE}/knowledge/staged`, form, {
            headers: multipartAuthHeaders(),
            params: { domain },
            timeout: KNOWLEDGE_UPLOAD_TIMEOUT_MS,
        });
        return { ok: true, sources: asSources(res.data?.data ?? res.data) };
    } catch (err) {
        return knowledgeError(err, file.name);
    }
}

/**
 * A staged note.
 *
 * Sent as MULTIPART, unlike its business-scoped twin. `/knowledge/staged` takes
 * one form for both kinds — `files`, `title`, `text`, `label`, `content_as_of`
 * are all `File(...)`/`Form(...)` — where `/businesses/{id}/knowledge/text`
 * takes a Pydantic JSON body. Two routes, two transports; the FE has to match
 * each rather than pick one.
 */
export async function stageKnowledgeText(
    domain: string,
    input: { title: string; text: string; label?: string; content_as_of?: string },
): Promise<KnowledgeAddResult> {
    if (useFixtures) return devMock.stageKnowledgeText(domain, input);
    const form = new FormData();
    form.append("title", input.title);
    form.append("text", input.text);
    if (input.label) form.append("label", input.label);
    if (input.content_as_of) form.append("content_as_of", input.content_as_of);
    try {
        const res = await axios.post(`${AEO_BASE}/knowledge/staged`, form, {
            headers: multipartAuthHeaders(),
            params: { domain },
        });
        return { ok: true, sources: asSources(res.data?.data ?? res.data) };
    } catch (err) {
        return knowledgeError(err, input.title);
    }
}

/**
 * GET — what New scan staged for this domain, before its business row exists
 * (ourteam-backend#4994). The same live list the business route gives, reached
 * by domain, so it reads the same `{ sources, limits }` envelope.
 *
 * A failure is THROWN, never an empty list. An unreadable store shown as
 * `{ sources: [] }` reads as "nothing is staged", and the operator would start
 * the scan without seeing what the scan is about to use (ourteam-frontend#4760
 * review). The caller decides what to show and gates the start on it.
 */
export async function listStagedKnowledge(domain: string): Promise<KnowledgePage> {
    if (useFixtures) return devMock.listStagedKnowledge(domain);
    try {
        const res = await axios.get(`${AEO_BASE}/knowledge/staged`, { headers: authHeaders(), params: { domain } });
        const body = (res.data?.data ?? res.data) as { sources?: unknown; limits?: KnowledgeLimits } | null;
        // A 200 without the envelope is a failure too, not a confirmed empty list.
        if (!Array.isArray(body?.sources)) throw new Error("staged knowledge: response has no sources list");
        return { sources: body!.sources as AeoKnowledgeSource[], limits: body?.limits };
    } catch (err) {
        catchErrorLog(`[aeo] listStagedKnowledge failed for ${domain}: ${err}`);
        throw err;
    }
}

/**
 * DELETE — takes one staged source back before the scan adopts it. Same soft
 * delete as the business route. A refusal (403 not staff, 400 bad domain, 404
 * unknown / other site / already deleted) comes back with the server's own
 * sentence, so the screen can say it plainly.
 */
export async function deleteStagedKnowledge(sourceId: string, domain: string): Promise<StagedDeleteResult> {
    if (useFixtures) return devMock.deleteStagedKnowledge(sourceId, domain);
    try {
        await axios.delete(`${AEO_BASE}/knowledge/staged/${sourceId}`, { headers: authHeaders(), params: { domain } });
        return { ok: true };
    } catch (err) {
        catchErrorLog(`[aeo] deleteStagedKnowledge failed for ${sourceId}: ${err}`);
        const { message } = readApiError(err, "It could not be removed. Nothing was changed.");
        return { ok: false, message };
    }
}

/* ── Partial re-run (ourteam-backend#5059) ────────────────────────────────── */

/**
 * POST /api/v1/assessments/{id}/rerun — re-execute named stages of an existing
 * assessment instead of paying for a whole one.
 *
 * ONE ENDPOINT, EVERY CARD. #4995 (readability), #4352 (PageSpeed) and #5028
 * (backfill the AI Overviews row) each solved one instance of this by hand;
 * ourteam-backend#5056/#5057/#5058 proposed three more. This is the shape those
 * either wrap or replace, and keeping the whole contract in one function is
 * what makes the choice a service-layer edit rather than a rewrite of four
 * screens.
 *
 * The outcomes are the same four `startReadabilityRefresh` established: `busy`
 * is the 409 (an assessment is already running), `absent` is the 404/405 that
 * says this deployment has no such route and the control should remove itself,
 * `forbidden` is a session that may not do this at all, and anything else is a
 * failure to say out loud.
 */
export async function startStageRerun(
    assessmentId: string,
    stages: string[],
): Promise<
    | ({ ok: true } & AeoStageRerunStart)
    | { ok: false; kind: "busy" | "forbidden" | "error"; message: string }
    | { ok: false; kind: "absent" }
> {
    if (useFixtures) return devMock.startStageRerun(assessmentId, stages);
    try {
        const res = await axios.post(
            `${AEO_BASE}/assessments/${assessmentId}/rerun`,
            { stages },
            { headers: authHeaders() },
        );
        const body = (res.data?.data ?? res.data) as AeoStageRerunStart;
        return {
            ok: true,
            jobId: body?.jobId ?? null,
            stages: body?.stages ?? stages,
            kind: body?.kind ?? null,
            estimatedCostUsd: typeof body?.estimatedCostUsd === "number" ? body.estimatedCostUsd : null,
        };
    } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 404 || status === 405) return { ok: false, kind: "absent" };
        if (status === 403) return { ok: false, kind: "forbidden", message: "This login may not re-run a scan." };
        // The envelope carries `message`; some routes use `detail`. Both are the
        // backend's own sentence and both are rendered verbatim.
        const body = axios.isAxiosError(err)
            ? (err.response?.data as { message?: string; detail?: string } | undefined)
            : undefined;
        const said = body?.message || body?.detail;
        if (status === 409) {
            return { ok: false, kind: "busy", message: said || "A scan for this business is already running." };
        }
        // 422 `stage_not_rerunnable` — this build asked for a stage the backend
        // does not re-run, and its refusal NAMES the ones it does:
        // "Unknown stage 'x'. This endpoint re-runs: finalize, crawl, gsc, ga4,
        // pagespeed." Rendered as it came, because the authoritative list is
        // the backend's, not a copy kept here.
        if (status === 422) {
            catchErrorLog(`[aeo] stage rerun refused for ${assessmentId}: ${said}`);
            return { ok: false, kind: "error", message: said || "That stage cannot be re-run on its own." };
        }
        catchErrorLog(`[aeo] stage rerun failed for ${assessmentId}: ${err}`);
        return { ok: false, kind: "error", message: said || "The re-run could not be started." };
    }
}

/**
 * GET /api/v1/assessments/{id}/rerun/{jobId}.
 *
 * `"gone"` is an id the backend no longer knows — not a failure, just nothing
 * left to follow. `null` is a dropped request, and the poll loop keeps asking.
 */
export async function getStageRerun(
    assessmentId: string,
    jobId: string,
): Promise<AeoStageRerunJob | "gone" | null> {
    if (useFixtures) return devMock.getStageRerun(assessmentId, jobId);
    try {
        const res = await axios.get(`${AEO_BASE}/assessments/${assessmentId}/rerun/${jobId}`, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoStageRerunJob;
    } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 404 || status === 410) return "gone";
        return null;
    }
}

/* ── Google AI refresh (ourteam-backend#5037/#5067) ───────────────────────── */

/** The envelope's own sentence, whichever field this route put it in. */
function saidBy(err: unknown): { said?: string; errs: string[]; data?: Record<string, unknown> } {
    if (!axios.isAxiosError(err)) return { errs: [] };
    const body = err.response?.data as
        { message?: string; detail?: string; errors?: string[]; data?: Record<string, unknown> } | undefined;
    return {
        said: body?.message || body?.detail,
        errs: Array.isArray(body?.errors) ? body!.errors! : [],
        data: body?.data,
    };
}

/**
 * POST /api/v1/businesses/{id}/google-ai/refresh — read Google's AI Overviews
 * for this business's questions, without a rescan.
 *
 * MINUTES, NOT SECONDS. The browser types each question and waits between
 * searches, so a 50-question read runs 8 to 15 minutes. Nothing here should be
 * given a short client-side timeout.
 *
 * The 409s are the interesting part, and they are two different things. A
 * refresh already in flight hands back its `jobId` — that is an instruction to
 * ATTACH, not an error to report. A full scan in flight is a refusal, because
 * the scan writes its own Google AI row and a second read would race it.
 */
export async function startGoogleAiRefresh(businessId: string): Promise<AeoGoogleAiRefreshStart> {
    if (useFixtures) return devMock.startGoogleAiRefresh(businessId);
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${businessId}/google-ai/refresh`, null, {
            headers: authHeaders(),
        });
        const body = (res.data?.data ?? res.data) as { jobId?: string; assessmentId?: string; questions?: number };
        if (!body?.jobId) return { ok: false, kind: "error", message: "The read started but reported no job to follow." };
        return { ok: true, jobId: body.jobId, assessmentId: body.assessmentId ?? null, questions: body.questions ?? null };
    } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const { said, errs, data } = saidBy(err);
        if (status === 404 || status === 405) return { ok: false, kind: "absent" };
        if (status === 403) return { ok: false, kind: "absent" };
        if (status === 409) {
            if (errs.indexOf("google_ai_refresh_in_flight") >= 0) {
                return {
                    ok: false,
                    kind: "busy",
                    message: said || "A read is already running.",
                    jobId: (data?.jobId as string | undefined) ?? null,
                };
            }
            return { ok: false, kind: "scanning", message: said || "A scan is running — it will include Google AI." };
        }
        if (status === 503 || status === 422) {
            return {
                ok: false,
                kind: "unavailable",
                message: said || "Google AI cannot be read for this business yet.",
                reason: errs[0] || String(status),
            };
        }
        catchErrorLog(`[aeo] google-ai refresh failed for ${businessId}: ${err}`);
        return { ok: false, kind: "error", message: said || "The read could not be started." };
    }
}

/**
 * GET /api/v1/businesses/{id}/google-ai/refresh — what is running, if anything.
 *
 * Called on screen load so a reload, or a second staffer opening the same
 * business, attaches to the read already in flight instead of starting a
 * second one. A read costs proxy bandwidth and takes a quarter of an hour;
 * two of them racing is the thing this call exists to prevent.
 */
export async function getGoogleAiRefreshState(businessId: string): Promise<AeoGoogleAiRefreshState | null> {
    if (useFixtures) return devMock.getGoogleAiRefreshState(businessId);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/google-ai/refresh`, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoGoogleAiRefreshState;
    } catch {
        // Absent route, a client session, or a dropped request — all mean "do
        // not attach to anything", and none is worth saying out loud here.
        return null;
    }
}

/**
 * GET /api/v1/businesses/{id}/google-ai/refresh/{jobId}.
 *
 * `"gone"` is the 404: jobs expire an hour after they finish, so the answer is
 * to re-read the business rather than to report a failure.
 */
export async function getGoogleAiRefreshJob(
    businessId: string,
    jobId: string,
): Promise<AeoGoogleAiRefreshJob | "gone" | null> {
    if (useFixtures) return devMock.getGoogleAiRefreshJob(businessId, jobId);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${businessId}/google-ai/refresh/${jobId}`, {
            headers: authHeaders(),
        });
        return (res.data?.data ?? res.data) as AeoGoogleAiRefreshJob;
    } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 404 || status === 410) return "gone";
        return null;
    }
}
